import { useEffect, useMemo, useState } from 'react'
import type { ArcadeTheme } from './themes'
import { STATUS_COLORS } from './themes'
import {
  BumpChart,
  ChartLegend,
  Donut,
  Dumbbell,
  Heatmap,
  LineChart,
  NEUTRAL_SERIES,
  RaceTrack,
  RankedBars,
  SmallMultiple,
  fmtInt,
  fmtMs,
  fmtPct,
  slotColor,
} from './charts'
import { TopicShareBar } from './atoms'
import {
  buildBump,
  buildFixture,
  buildLeague,
  buildRivalry,
  buildRace,
  buildTrack,
  defaultFixture,
  fmtTokens,
  outlookFor,
  sameSide,
  supportsDailySeries,
  weeksAvailable,
  type BoardModel,
  type BoardPlayer,
  type FixtureSide,
  type LeagueDimension,
  type LeagueSelection,
  type McpFamilyTotals,
  type RivalryStanding,
  type ToolFamily,
} from './viewModel'

/**
 * The four reworked arcade views.
 *
 * WHY A MODULE OF THEIR OWN. `pages/Leaderboard.tsx` owns three things — the
 * ONE fetch, the shell, and the dispatch — and everything else is delegated.
 * These four views are large enough that leaving them inline would have made
 * that file the thing it was split up to stop being.
 *
 * All four take a `BoardModel`, which has NO `spend` field and no raw `move`,
 * so none of them can put a withheld figure on screen and none of them needs a
 * hide-costs branch. See `arcade/viewModel.ts`.
 */

/** How many tools the leaderboard shows before the table gets a scroll. */
const TOOL_ROWS = 18
/** Series cap. Past six the palette would have to generate a hue, and a
 *  seventh generated hue is indistinguishable from an existing one under
 *  colour-vision deficiency, so the caller pools instead. */
const MAX_SERIES = 6
/** How many topics get their own small multiple on the landing view. The full
 *  set is on the Taxonomy view; a landing panel is a headline, not an index. */
const TREND_TOPICS = 5

function Card({
  title,
  sub,
  note,
  right,
  children,
}: {
  title: string
  sub?: string
  note?: React.ReactNode
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="card">
      <div className="cardhead">
        <div>
          <div className="ctitle">{title}</div>
          {sub ? <div className="csub">{sub}</div> : null}
        </div>
        {right}
      </div>
      {children}
      {note ? <div className="cnote">{note}</div> : null}
    </div>
  )
}

function Kpi({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="kpi">
      <div className="k">{label}</div>
      <div className="v">{value}</div>
      <div className="d">{detail}</div>
    </div>
  )
}

/**
 * The honest empty state for a chart the SELECTED WINDOW cannot support.
 *
 * WHY THIS COMPONENT EXISTS AT ALL. Until 31 Aug 2026 the race and the
 * platform trend drew a fixed three weeks of x-axis whatever the window pill
 * said, so ROLLING 24H produced a chart labelled 08-11 to 08-31 with twenty
 * structurally empty days in it. A chart that silently ignores the control
 * above it is worse than no chart: it is a confident answer to a question
 * nobody asked. The two honest options for a window too short to draw are to
 * say so, or to widen the range and say THAT — and widening it puts days on
 * screen the totals beside it do not contain, so this is the one that ships.
 */
function WindowTooShort({ form, model }: { form: string; model: BoardModel }) {
  return (
    <p className="hint">
      NO DAY-BY-DAY {form} IN A {model.window.toUpperCase()} WINDOW. It covers{' '}
      {model.days.length === 1 ? 'one calendar day' : `${model.days.length} calendar days`}, and this
      chart needs at least two. The KPI figures above and the leaderboard are still this window&rsquo;s
      — it is the SERIES that does not exist, not the data. Pick WK, MO or ALL to draw one.
    </p>
  )
}

/** Printed under any daily chart on the ALL window, where the totals cover a
 *  year and the line covers what the payload can carry. Silence here would let
 *  the line be read as covering the same period as the number beside it. */
function TruncationNote({ model }: { model: BoardModel }) {
  if (!model.daysTruncated) return null
  return (
    <>
      {' '}
      <b>The line is shorter than the window.</b> ALL totals a year; the daily buckets stop at{' '}
      {model.days.length} days, so the chart is the recent end of the period the figures cover, not
      the whole of it.
    </>
  )
}

/* ==========================================================================
 * 1. TOOLS / MCP
 * ======================================================================== */

/**
 * The tools view, once the platform is actually instrumented.
 *
 * WHAT THIS PANEL USED TO SAY: "Nothing is recording which MCP servers a
 * conversation used yet — this is a gap in the platform's instrumentation, not
 * a quiet week." That sentence was true of `conversations.mcpServers`, the one
 * field the panel was reading, and it is still zero on every conversation. It
 * was already wrong about the PLATFORM on the day it shipped: the ASGI emitter
 * had gone live the same week, and the message tool parts had carried the
 * answer since 11 Aug. The panel was reading the only place the data was not.
 *
 * TWO CHARTS, NEVER TWO AXES. Server volume and server reach are drawn side by
 * side with one scale each. A dual-axis version of that pair invents a
 * correlation between "loud" and "useful" that the data does not contain,
 * which is the single reading this panel exists to prevent.
 */
/** How a family is named on screen. Fixed, and never the raw family key: the
 *  whole failure this replaces was a bucket labelled with an internal token. */
const FAMILY_LABEL: Record<ToolFamily, string> = {
  mcp: 'MCP servers',
  code: 'Code interpreter',
  builtin: 'Other built-ins',
  handoff: 'Agent handoffs',
  unknown: 'Unrecognised',
}

/**
 * THE CAUTION THAT TRAVELS WITH THE NUMBER.
 *
 * A code-interpreter session is many small calls — measured 31 Aug 2026,
 * `bash_tool` ran 1,113 calls across 54 conversations, about 21 per
 * conversation — while `query_dwh` is one call that may run a warehouse query
 * over millions of rows. Ranking the two on CALLS says the sandbox is "more
 * used" than the warehouse in a sense that does not exist. This sentence is
 * rendered beside every chart that puts the families on one axis, never in a
 * footnote on another tab, because a caution a reader has to go and find is a
 * caution that did not happen.
 */
function NotLikeForLike() {
  return (
    <>
      {/* "Figures", not "bars": this sentence is rendered under a bar chart on
          one tab and under a table on two others, and a caution that names the
          wrong mark is a caution a reader stops trusting. */}
      <b>These figures are not like for like, and the difference is not small.</b> A code interpreter
      session is naturally dozens of small calls; one <code>query_dwh</code> is a single call that
      may do far more work than all of them. Calls measure HOW OFTEN a thing was reached for, never
      how much it did — so read them as frequency and nothing else. The calls-per-conversation
      column on the CODE INTERPRETER tab is there to keep that visible.
    </>
  )
}

export function ToolsView({ model, theme, tab }: { model: BoardModel; theme: ArcadeTheme; tab: string }) {
  const mcp = model.mcp
  const colors = theme.chartColors

  if (!mcp.available) {
    return (
      <div className="banner">
        <b>MCP USAGE COULD NOT BE READ.</b> The three nightly tables
        (<code>daily_mcp_tool_usage</code>, <code>daily_mcp_server_reach</code>,{' '}
        <code>daily_mcp_decision</code>) did not answer. That is a missing table or a missing
        GRANT, not a quiet week — this platform fails closed and silently, so the difference has
        to be stated rather than drawn as a zero. Agent usage below is unaffected.
      </div>
    )
  }
  if (mcp.servers.length === 0) {
    return (
      <p className="hint">
        NO MCP TOOL CALLS IN THIS WINDOW. The tables answered; they are empty for these days.
      </p>
    )
  }

  const denied = mcp.daily.reduce((n, d) => n + d.denied, 0)
  const errors = mcp.tools.reduce((n, t) => n + t.errors, 0)
  const withLatency = mcp.tools.filter((t) => t.p90Ms !== null)
  const slowest = [...withLatency].sort((a, b) => (b.p90Ms ?? 0) - (a.p90Ms ?? 0))[0] ?? null
  const typicalP50 = median(withLatency.map((t) => t.p50Ms ?? 0))

  const latencyRows = [...mcp.servers]
    .map((s) => {
      const own = mcp.tools.filter((t) => t.server === s.server && t.p50Ms !== null)
      if (own.length === 0) return null
      return {
        label: s.server,
        low: median(own.map((t) => t.p50Ms ?? 0)),
        high: median(own.map((t) => t.p90Ms ?? 0)),
      }
    })
    .filter((r): r is { label: string; low: number; high: number } => r !== null)
    .sort((a, b) => b.high - a.high)

  // ---- the families ----------------------------------------------------
  const family = (f: ToolFamily): McpFamilyTotals | undefined => mcp.families.find((x) => x.family === f)
  const code = family('code')
  const codeTools = mcp.tools.filter((t) => t.family === 'code')
  const builtinTools = mcp.tools.filter((t) => t.family === 'builtin')
  const unknownTools = mcp.tools.filter((t) => t.family === 'unknown')
  const familyRows = mcp.families.map((f) => ({
    label: FAMILY_LABEL[f.family],
    value: f.calls,
    note: `${f.tools} ${f.tools === 1 ? 'tool' : 'tools'} · ${f.people} ${f.people === 1 ? 'person' : 'people'}`,
  }))

  // The family trend. Its own day list, from the tool table the server already
  // bounded by the window — NOT `model.days`, which is the spend-log axis.
  const familyDays = [...new Set(mcp.familyDaily.map((d) => d.day))].sort()
  const familySeries = mcp.families.map((f, i) => {
    const byDay = new Map(mcp.familyDaily.filter((d) => d.family === f.family).map((d) => [d.day, d.calls]))
    return {
      name: FAMILY_LABEL[f.family],
      values: familyDays.map((d) => byDay.get(d) ?? 0),
      color: slotColor(colors, i),
    }
  })

  if (tab === 'summary') {
    return (
      <>
        <div className="banner">
          <b>NOW INSTRUMENTED, AND NOW CORRECTLY LABELLED.</b> Every MCP tool call has been recorded
          since 29 Aug 2026 by the shared ASGI ingress, which covers LibreChat, the Teams bot and
          n8n. The history before that is reconstructed from the message tool parts, which reach
          back to 11 Aug. As of 31 Aug the panel also stops calling LibreChat&rsquo;s own built-in
          tools an MCP server: they were pooled into a bucket named <code>(unknown)</code>, and the
          largest thing in it is the most-used capability on the platform.
        </div>

        <div className="kpis">
          <Kpi label="Tool calls" value={fmtInt(mcp.totalCalls)} detail="every family, calls sum freely" />
          <Kpi
            label="People reaching for one"
            value={fmtInt(mcp.totalPeople)}
            detail={`of ${model.org.players} active`}
          />
          <Kpi
            label="Code interpreter calls"
            value={fmtInt(code?.calls ?? 0)}
            detail={code ? `${code.people} of ${model.org.players} people` : 'none this window'}
          />
          <Kpi
            label="MCP server calls"
            value={fmtInt(family('mcp')?.calls ?? 0)}
            detail={`${mcp.servers.length} servers reached`}
          />
          <Kpi label="Tools ever called" value={fmtInt(mcp.totalTools)} detail="distinct, this window" />
          <Kpi
            label="Denied"
            value={fmtInt(denied)}
            detail={denied === 0 ? 'no refusals recorded' : 'read as unmet demand'}
          />
          <Kpi
            label="Calls returning an error"
            value={fmtInt(errors)}
            detail={mcp.totalCalls > 0 ? `${fmtPct(errors / mcp.totalCalls)} · MCP only` : 'no calls'}
          />
          <Kpi
            label="Typical call"
            value={typicalP50 === null ? '—' : fmtMs(typicalP50)}
            detail={slowest?.p90Ms ? `slowest tool p90 ${fmtMs(slowest.p90Ms)}` : 'from the emitter'}
          />
        </div>

        <Card
          title="What a tool call actually is"
          sub="Three different things were being counted as one. This is the split, and it is the point of the whole panel: an MCP server, LibreChat's code interpreter, and LibreChat's other built-ins are not the same kind of object."
          note={<NotLikeForLike />}
        >
          <RankedBars rows={familyRows} colors={colors} label="Tool calls by family" />
          <p className="csub" style={{ marginTop: 14 }}>
            <b>Agent handoffs are counted here and are not tools.</b>{' '}
            <code>lc_transfer_to_agent_&lt;id&gt;</code> is LibreChat moving a conversation from one
            agent to another; nobody chose to call it. They are on the BUILT-INS tab with the agent
            they went to. Anything genuinely unrecognised stays in its own row rather than being
            folded into a neighbour — a new LibreChat tool appearing there is a signal, and it only
            works while that row stays small.
          </p>
        </Card>

        <Card
          title="Built-in against MCP, over time"
          sub="The one comparison worth drawing on a shared axis: both series are calls, and the question is which way each is moving."
          note={
            <>
              Same axis because both are the same unit; the <b>heights are still not comparable
              work</b> for the reason above. This series is bounded by the window pill like every
              other chart here, and the tool tables are a NIGHTLY batch, so a sub-day window reads
              one day and cannot draw a line at all.
            </>
          }
        >
          {familyDays.length > 1 ? (
            <>
              <LineChart
                series={familySeries}
                xLabels={familyDays.map((d) => d.slice(5))}
                label="Tool calls per day by family"
              />
              <ChartLegend shape="line" items={familySeries.map((s) => ({ label: s.name, color: s.color }))} />
            </>
          ) : (
            <p className="hint">
              NO DAY-BY-DAY SERIES IN A {model.window.toUpperCase()} WINDOW. The tool tables are
              written once a night, so every window shorter than a day reads the same single day.
              The totals above are that day. Pick WK, MO or ALL.
            </p>
          )}
        </Card>
      </>
    )
  }

  if (tab === 'code') {
    return <CodeInterpreterPanels model={model} theme={theme} totals={code} tools={codeTools} />
  }

  if (tab === 'builtins') {
    return (
      <>
        <Card
          title="LibreChat's other built-in tools"
          sub="Web search, artifacts, skills, conversation search. Not MCP servers, not granted by anybody, available to every signed-in person by default."
          note={
            <>
              <b>Every emitter column is blank here and always will be.</b> Latency, HTTP status,
              denials and the calling plane come from our own ASGI ingress, and a LibreChat built-in
              never passes through it — confirmed against the decision table, which has never held a
              single row for one of these names. That is a structural absence, so the columns are
              left empty rather than filled with a zero that would read as &ldquo;never
              failed&rdquo;.
            </>
          }
        >
          {builtinTools.length === 0 ? (
            <p className="hint">NO BUILT-IN TOOL CALLS IN THIS WINDOW.</p>
          ) : (
            <BuiltinTable tools={builtinTools} />
          )}
        </Card>

        <Card
          title="Agent handoffs"
          sub="One agent passing a conversation to another. A control-flow event, counted as a handoff rather than as a tool call so it cannot inflate a tool leaderboard."
          note={
            <>
              The id in <code>lc_transfer_to_agent_&lt;id&gt;</code> is LibreChat&rsquo;s agent
              nanoid, resolved against the agents collection. A handoff to an agent that has since
              been DELETED keeps a blank name and its count: dropping it would understate what
              happened.
            </>
          }
        >
          {mcp.handoffs.length === 0 ? (
            <p className="hint">NO HANDOFFS RECORDED IN THIS WINDOW.</p>
          ) : (
            <div className="tablewrap">
              <table className="chtable narrow">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th className="num">Handoffs</th>
                    <th className="num">People</th>
                  </tr>
                </thead>
                <tbody>
                  {mcp.handoffs.map((h) => (
                    <tr key={h.agentId}>
                      <td>{h.agent ? <b>{h.agent}</b> : <span className="chnote">deleted agent</span>}</td>
                      <td className="num">{fmtInt(h.calls)}</td>
                      <td className="num">{fmtInt(h.people)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {unknownTools.length > 0 ? (
          <Card
            title="Unrecognised"
            sub="Names that are neither an MCP tool nor a built-in this panel knows about."
            note="Kept visible on purpose. This row is how a new LibreChat tool, or a naming change that stops the server suffix parsing, becomes noticeable instead of quietly joining a bucket. It is supposed to stay empty."
          >
            <BuiltinTable tools={unknownTools} />
          </Card>
        ) : null}
      </>
    )
  }

  if (tab === 'servers') {
    return (
      <>
        <Card
        title="Servers · volume and reach"
        sub="Two questions, two charts, one axis each. Volume says what is being hammered. Reach says what the business actually depends on."
        note={
          <>
            Reach is deliberately <b>not</b> drawn on the same axis as calls. A dual-axis version
            of this chart invents a correlation between &ldquo;loud&rdquo; and &ldquo;useful&rdquo;
            that the data does not contain, which is the exact reading this panel exists to prevent.
          </>
        }
      >
        <div className="split">
          <div>
            <p className="dh">Calls</p>
            <RankedBars
              rows={mcp.servers.map((s) => ({ label: s.server, value: s.calls }))}
              colors={colors}
              label="MCP tool calls by server"
            />
          </div>
          <div>
            <p className="dh">People who used it</p>
            <RankedBars
              rows={mcp.servers.map((s) => ({ label: s.server, value: s.people, note: 'people' }))}
              colors={[slotColor(colors, 2)]}
              label="Distinct people per MCP server"
            />
          </div>
        </div>
      </Card>

      <Card
        title="Breadth · how much of each server is actually touched"
        sub="Distinct tools of each server that saw a call in this window."
        note={
          <>
            <b>This is a count, not a coverage ratio, and the difference matters.</b> The honest
            version of this panel is &ldquo;61 of 87 tools have ever been called&rdquo; — and the
            denominator, the registry of defined tools, lives in terraform rather than in the
            warehouse. Drawing a ratio of tools-seen over tools-seen would put every bar at 100%
            and say nothing at all, so the panel draws the number it has and names the number it
            does not.
            <br />
            <br />
            <b>The <code>(unknown)</code> bar has left this chart, and that is the 31 Aug fix.</b>{' '}
            It was thirteen tools that are not MCP servers at all, drawn on a chart about MCP
            servers. They are on the CODE INTERPRETER and BUILT-INS tabs, under their own names.
          </>
        }
      >
        <RankedBars
          rows={mcp.servers.map((s) => ({ label: s.server, value: s.tools, note: 'tools' }))}
          colors={[slotColor(colors, 2)]}
          label="Distinct tools called per MCP server"
        />
      </Card>
      </>
    )
  }

  if (tab === 'governance') {
    return (
      <>
      <Card
        title="Denied calls · the governance signal"
        sub="Access fails closed by design: a wrong grant name produces no error, no alarm and no log line, and the tools are simply never offered. A denial is the only way that becomes visible. Read as unmet demand, never as misbehaviour."
        note={
          <>
            Allowed and denied are drawn as <b>two charts on two scales</b>, not one chart with
            the allowed series &ldquo;scaled to fit&rdquo;. Scaling one series to fit another is a
            second axis wearing a disguise.
          </>
        }
      >
        <div className="split">
          <div>
            <p className="dh">Denied per day</p>
            <RankedBars
              rows={mcp.daily.map((d) => ({ label: d.day.slice(5), value: d.denied }))}
              colors={[STATUS_COLORS.critical]}
              label="Denied MCP calls per day"
            />
            <p className="dh" style={{ marginTop: 16 }}>
              Allowed per day
            </p>
            <RankedBars
              rows={mcp.daily.map((d) => ({ label: d.day.slice(5), value: d.allowed }))}
              colors={[slotColor(colors, 0)]}
              label="Allowed MCP calls per day"
            />
          </div>
          <div>
            <p className="dh">Every denial this window</p>
            {mcp.denials.length === 0 ? (
              <p className="hint">
                NO DENIALS RECORDED. Fail-closed is silent by design, so a zero here is worth
                keeping an eye on rather than celebrating: it is also what a broken emitter looks
                like.
              </p>
            ) : (
              <div className="tablewrap">
                <table className="chtable narrow">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Server</th>
                      <th>Tool</th>
                      <th>Plane</th>
                      <th className="num">Calls</th>
                      <th className="num">People</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mcp.denials.map((d) => (
                      <tr key={`${d.day}-${d.server}-${d.tool}-${d.source}`}>
                        <td>{d.day}</td>
                        <td>
                          <span className="status">
                            <i style={{ background: STATUS_COLORS.critical }} />
                            {d.server}
                          </span>
                        </td>
                        <td>
                          <b>{d.tool}</b>
                        </td>
                        <td>{d.source}</td>
                        <td className="num">{fmtInt(d.calls)}</td>
                        <td className="num">{fmtInt(d.people)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="csub" style={{ marginTop: 12 }}>
              A denial is a COUNT here and never a name: the decision table stores neither an email
              nor an id, so there is nothing to expand into a person. A burst is the interesting
              case — either somebody reaching for a server they are not granted, or a grant that
              has just broken and is now failing closed for everyone.
            </p>
          </div>
        </div>
      </Card>

      <Card
        title="How long a call takes"
        sub="Median to 90th percentile per server. The right-hand dot is the number that decides whether people keep using a tool."
        note={
          <>
            From the emitter&rsquo;s duration field, which Mongo cannot supply — which is why both
            feeds are kept. These are the <b>typical day&rsquo;s</b> percentiles, not the
            window&rsquo;s: a percentile cannot be recomputed from stored percentiles, and
            labelling a median-of-p90s as a p90 would be a quiet lie.
          </>
        }
      >
        {latencyRows.length === 0 ? (
          <p className="hint">
            NO LATENCY DATA IN THIS WINDOW. The emitter went live on 29 Aug 2026, so anything
            earlier has volume but no duration.
          </p>
        ) : (
          <>
            <Dumbbell rows={latencyRows} colors={colors} />
            <ChartLegend
              items={[
                { label: 'median (typical day)', color: slotColor(colors, 0) },
                { label: '90th percentile (typical day)', color: slotColor(colors, 2) },
              ]}
            />
          </>
        )}
      </Card>
      </>
    )
  }

  if (tab === 'tools') {
    return (
      <Card
        title="Every tool, every family"
        sub="Which tools are actually in play, by calls and by how many people reached for them. The FAMILY column is the one that stops this table lying: five of these rows are not MCP tools."
        note={
          <>
            <b>Chats does not sum down this column.</b> One conversation that used four tools
            appears on four rows. The server totals on the MCP SERVERS tab are counted at server
            grain for exactly that reason.
            <br />
            <br />
            <b>p50, p90 and errors are blank for every non-MCP row, structurally.</b> Those come
            from our own ASGI ingress and a LibreChat built-in never reaches it. A dash means not
            measured; it does not mean fast, and it does not mean it never failed.
            <br />
            <br />
            <NotLikeForLike />
          </>
        }
      >
        <div className="tablewrap">
          <table className="chtable">
            <thead>
              <tr>
                <th>Tool</th>
                <th>Family</th>
                <th>Server</th>
                <th className="num">Calls</th>
                <th className="num">People</th>
                <th className="num">Chats</th>
                <th className="num">p50</th>
                <th className="num">p90</th>
                <th className="num">Errors</th>
                <th>Plane</th>
              </tr>
            </thead>
            <tbody>
              {mcp.tools.slice(0, TOOL_ROWS).map((t) => (
                <tr key={`${t.server}-${t.tool}`}>
                  <td>
                    <b>{t.tool}</b>
                  </td>
                  <td>{FAMILY_LABEL[t.family]}</td>
                  <td>{t.family === 'mcp' ? t.server : '—'}</td>
                  <td className="num">{fmtInt(t.calls)}</td>
                  <td className="num">{fmtInt(t.people)}</td>
                  <td className="num">{fmtInt(t.conversations)}</td>
                  <td className="num">{t.p50Ms === null ? '—' : fmtMs(t.p50Ms)}</td>
                  <td className="num">{t.p90Ms === null ? '—' : fmtMs(t.p90Ms)}</td>
                  <td className="num">{t.errors === 0 ? '—' : fmtInt(t.errors)}</td>
                  <td>{t.planes.length === 0 ? '—' : t.planes.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card
        title="Where this comes from"
        sub="Two feeds, kept separate on purpose. Neither can answer the other's questions."
      >
        <div className="method">
          <div>
            <p className="dh">The emitter · live since 29 Aug 2026</p>
            <table>
              <tbody>
                <tr>
                  <td>Where</td>
                  <td>
                    <code>docker/_shared/chugpt_usage</code>, the shared ASGI ingress
                  </td>
                </tr>
                <tr>
                  <td>Covers</td>
                  <td>LibreChat, the Teams bot, n8n. Every calling plane.</td>
                </tr>
                <tr>
                  <td>Only it has</td>
                  <td>
                    <b>denials</b>, latency, HTTP status, calling plane
                  </td>
                </tr>
                <tr>
                  <td>It lacks</td>
                  <td>a conversation id, an agent id, and any history before 29 Aug</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <p className="dh">The message parts · full history</p>
            <table>
              <tbody>
                <tr>
                  <td>Where</td>
                  <td>
                    Mongo <code>messages.content[].tool_call.name</code>
                  </td>
                </tr>
                <tr>
                  <td>Covers</td>
                  <td>LibreChat only</td>
                </tr>
                <tr>
                  <td>Only it has</td>
                  <td>
                    <b>conversation linkage</b>, and everything since 11 Aug
                  </td>
                </tr>
                <tr>
                  <td>Careful</td>
                  <td>
                    <b>
                      the same part carries <code>args</code> and <code>output</code>, which hold
                      live claim, policy and finance data. Only <code>name</code> is ever read, and
                      only by the nightly export.
                    </b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p className="csub" style={{ marginTop: 18 }}>
          <b>Which feed covers which family, because it decides what a blank column means.</b> MCP
          tools are in BOTH feeds, so they carry latency, denials and a calling plane. The code
          interpreter, the other built-ins and the agent handoffs are in the MESSAGE PARTS ONLY:
          they never pass through our ASGI ingress, so the emitter has never recorded one — checked
          against the decision table, which holds no row for any of those names and no{' '}
          <code>(unknown)</code> server at all. Their p50, p90, error and plane columns are
          therefore blank BY CONSTRUCTION, and a zero there would be a measurement nobody took.
        </p>
        <p className="csub" style={{ marginTop: 12 }}>
          <b>Two spellings had to be reconciled before either feed could be trusted.</b> LibreChat
          suffixes a tool name with its own registry key while the server emits under its GRANT id,
          so <code>docgen</code>/<code>chu-docgen</code> and <code>m365</code>/<code>chu-m365</code>{' '}
          arrived as four servers instead of two, and <code>dwh</code> is an older key for what is
          now <code>chu-data</code>. Separately, one tool suffixed with a HYPHEN
          (<code>search_sharepoint_mcp-m365</code>) never split at all and sat in the unknown bucket
          for the life of the table. Both are fixed at read time and the export no longer creates
          the second.
        </p>
      </Card>

      {mcp.planes.length > 0 ? (
        <Card
          title="Which plane called"
          sub="LibreChat against the Teams bot against n8n. Nobody on this platform has ever seen this split."
          note="From the emitter only, because it is the only feed that sees a plane other than LibreChat at all."
        >
          <RankedBars
            rows={mcp.planes.map((p) => ({ label: p.source, value: p.calls }))}
            colors={colors}
            monochrome
            label="MCP calls by calling plane"
          />
        </Card>
      ) : null}
    </>
  )
}

/**
 * The table shape shared by the built-in families.
 *
 * FOUR COLUMNS ARE DELIBERATELY ABSENT rather than present-and-empty: latency,
 * error count and calling plane do not exist for any of these rows and never
 * will, so a column of dashes would be nine-tenths of the table's width spent
 * saying "no". The single sentence above the table says it once instead. What
 * IS here is calls per conversation, because that is the number that stops a
 * sandbox session being compared to a warehouse query as if they were the same
 * unit of work.
 */
function BuiltinTable({ tools }: { tools: BoardModel['mcp']['tools'] }) {
  return (
    <div className="tablewrap">
      <table className="chtable narrow">
        <thead>
          <tr>
            <th>Tool</th>
            <th className="num">Calls</th>
            <th className="num">People</th>
            <th className="num">Chats</th>
            <th className="num">Calls / chat</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((t) => (
            <tr key={t.tool}>
              <td>
                <b>{t.tool}</b>
              </td>
              <td className="num">{fmtInt(t.calls)}</td>
              <td className="num">{fmtInt(t.people)}</td>
              <td className="num">{t.conversations === 0 ? '—' : fmtInt(t.conversations)}</td>
              <td className="num">
                {t.conversations === 0 ? '—' : (t.calls / t.conversations).toFixed(1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * THE CODE INTERPRETER, given the room it earns.
 *
 * `bash_tool` alone ran 1,113 calls in the fortnight to 31 Aug 2026 — more
 * than every CHU MCP tool except the warehouse, and more than any single one of
 * them — and it spent that entire time inside a bar labelled `(unknown)` on a
 * panel about MCP servers. That is not a labelling nit: it is the platform's
 * most-used capability being invisible to the only view that reports usage.
 *
 * WHAT THIS PANEL WILL NOT CLAIM. Three questions were asked of it and one of
 * them cannot be answered from the warehouse, so it is named rather than
 * approximated — see the closing card.
 */
function CodeInterpreterPanels({
  model,
  theme,
  totals,
  tools,
}: {
  model: BoardModel
  theme: ArcadeTheme
  totals: McpFamilyTotals | undefined
  tools: BoardModel['mcp']['tools']
}) {
  const colors = theme.chartColors
  const mcpTotals = model.mcp.families.find((f) => f.family === 'mcp')
  const reach = model.mcp.builtinReach

  if (!totals || tools.length === 0) {
    return (
      <Card
        title="Code interpreter"
        sub="LibreChat's sandbox: bash, code execution, and the files it reads and writes."
      >
        <p className="hint">NO CODE INTERPRETER CALLS IN THIS WINDOW.</p>
      </Card>
    )
  }

  // The busiest single tool carries the calls-per-conversation headline,
  // because it is the one whose ratio is doing the distorting.
  const busiest = [...tools].sort((a, b) => b.calls - a.calls)[0]!
  const perChat = busiest.conversations > 0 ? busiest.calls / busiest.conversations : null

  return (
    <>
      <div className="banner">
        <b>THIS IS THE MOST-USED CAPABILITY ON THE PLATFORM, AND IT IS NOT AN MCP SERVER.</b>{' '}
        LibreChat&rsquo;s code interpreter runs in LibreChat, not in our cluster: no grant gates it,
        no server of ours sees it, and until 31 Aug 2026 every one of its calls was pooled into a
        bucket named <code>(unknown)</code> on the MCP panel.
      </div>

      <div className="kpis">
        <Kpi label="Calls" value={fmtInt(totals.calls)} detail={`across ${totals.tools} sandbox tools`} />
        <Kpi
          label="People who used it"
          value={fmtInt(totals.people)}
          detail={`of ${model.org.players} active — everyone has it`}
        />
        <Kpi
          label="Adoption"
          value={model.org.players > 0 ? fmtPct(totals.people / model.org.players) : '—'}
          detail="availability is universal, so this is real adoption"
        />
        <Kpi
          label={`${busiest.tool} per conversation`}
          value={perChat === null ? '—' : perChat.toFixed(1)}
          detail="why calls do not compare to an MCP call"
        />
        <Kpi
          label="MCP calls, for scale"
          value={mcpTotals ? fmtInt(mcpTotals.calls) : '—'}
          detail="frequency against frequency, never work against work"
        />
        <Kpi
          label="Conversations using a built-in"
          value={reach ? fmtInt(reach.conversations) : '—'}
          detail={reach ? `${reach.people} people · every built-in pooled` : 'reach table empty'}
        />
      </div>

      <Card
        title="Adoption, not volume"
        sub="Availability is the thing that makes this measurable at all: the code interpreter is not granted, gated or requested. Every signed-in person already has it, so the people who used it over the people who could is a genuine adoption rate rather than a licence count."
        note={
          <>
            <b>The denominator is people ACTIVE this window, not the tenant.</b> Somebody who never
            opened CHU-GPT this window has not declined to use the sandbox; they have not used
            anything. Dividing by 2,253 tenant accounts would produce a number that only measures
            how many people work here.
          </>
        }
      >
        {/* SHORT LABELS, and that is a constraint rather than a preference:
            `RankedBars` gives its category gutter a fixed 148px so every chart
            on the board aligns, and a longer phrase is overrun by the bar
            rather than wrapped. The sentence those labels wanted to be is in
            the card's subtitle, where there is room for it. */}
        <RankedBars
          rows={[
            { label: 'Code interpreter', value: totals.people, note: 'people' },
            { label: 'Any MCP tool', value: mcpTotals?.people ?? 0, note: 'people' },
            { label: 'Active at all', value: model.org.players, note: 'people' },
          ]}
          colors={colors}
          label="People, by what they reached for"
        />
      </Card>

      <Card
        title="The sandbox tools"
        sub="bash and code execution, plus the file surface that belongs to them. One sandbox session is many of these calls, which is what the last column is for."
        note={
          <>
            <NotLikeForLike />
            <br />
            <br />
            <b>Calls / chat is a FLOOR, not an exact ratio.</b> The conversation count sums across
            days, so a session spanning midnight is counted twice in the denominator and the true
            ratio is higher. It is also at tool grain, so the column does not add up: a conversation
            that ran <code>bash_tool</code> and <code>read_file</code> is on both rows.
            <br />
            <br />
            <b>The built-in <code>create_file</code> is not docgen&rsquo;s.</b> They share a name and
            nothing else — docgen&rsquo;s arrives suffixed with its server and is filed under it, and
            the two were checked against the live table rather than assumed apart.
          </>
        }
      >
        <BuiltinTable tools={tools} />
      </Card>

      <Card
        title="What this panel cannot tell you, and what it would take"
        sub="Asked and not answered, rather than approximated."
      >
        <div className="method">
          <div>
            <p className="dh">Sandbox alongside the warehouse, in one conversation</p>
            <p className="csub">
              The interesting workflow question — does somebody pull data with{' '}
              <code>query_dwh</code> and then compute on it in the sandbox, or are the two
              separate habits — <b>cannot be answered from the warehouse tables</b>. They store a
              distinct conversation COUNT per (day, server, tool, person) and no conversation id, so
              there is nothing to intersect. The linkage does exist in Mongo, where the nightly
              export reads it; answering this needs a fourth table at (day, conversation) grain
              recording which families that conversation touched. That is a schema change and a
              backfill, not a panel change, and drawing a guess in the meantime would be the same
              mistake as the bucket this panel just took apart.
            </p>
          </div>
          <div>
            <p className="dh">Failures and latency</p>
            <p className="csub">
              Blank, permanently, for everything on this tab. Our ASGI emitter records duration,
              HTTP status and denials for calls that reach a CHU MCP server; the code interpreter
              runs inside LibreChat and reaches none. The decision table has never held a row for
              one of these names. A zero in those columns would be a claim that nobody measured, so
              they are left out of the table entirely.
            </p>
            <p className="dh" style={{ marginTop: 16 }}>
              Cost
            </p>
            <p className="csub">
              Not available at any grain below the platform. Gateway spend attributes to a person, a
              model and a day, and carries neither a conversation nor a tool, so &ldquo;what does
              the sandbox cost&rdquo; has no answer that is not invented.
            </p>
          </div>
        </div>
      </Card>
    </>
  )
}

function median(xs: number[]): number | null {
  const clean = xs.filter((x) => Number.isFinite(x))
  if (clean.length === 0) return null
  const s = [...clean].sort((a, b) => a - b)
  return Math.round(s[Math.floor(s.length / 2)]!)
}

/* ==========================================================================
 * 2. THE LEAGUE (Team Wars / Origin)
 * ======================================================================== */

/**
 * A duel becomes a league.
 *
 * THE DIAGNOSIS WAS ARITHMETIC, NOT AESTHETIC. Under the old five-person floor
 * exactly ONE office cleared it and exactly TWO teams did, so the fixture
 * picker did not even render in the office grouping and every visit showed the
 * same two names forever. A ranked table of every selected group works at two
 * groups and at fourteen, and it survives the platform growing.
 *
 * The head-to-head arena is kept for the case it is genuinely good at —
 * exactly two groups selected — and it keeps the five rules that made it safe:
 * no individual appears, per active person, full saturation on both sides, the
 * gap always phrased from the trailing side and always positive, and unknowns
 * counted rather than hidden.
 */
export function LeagueView({ model, theme, tab }: { model: BoardModel; theme: ArcadeTheme; tab: string }) {
  const colors = theme.chartColors
  const [dimension, setDimension] = useState<LeagueDimension>('office')
  const [selection, setSelection] = useState<LeagueSelection | null>(null)
  const [floor, setFloor] = useState<number>(model.contestFloor)
  const [pooled, setPooled] = useState(false)

  // Every group in both dimensions, for the chips and the fixture pickers.
  // Passing the empty selection is now the documented way to ask for the
  // default, so this call and the one below cannot disagree about what the
  // default IS -- which is precisely how the league came to render nothing.
  const all = useMemo(
    () => buildLeague(model.ranked, { offices: [], teams: [] }, floor, dimension),
    [model.ranked, floor, dimension],
  )

  // An explicit selection wins; anything else is the default, computed in ONE
  // place (`buildLeague`) rather than here. An empty explicit selection is
  // deliberately NOT a state: emptying the chips returns the default rather
  // than a blank table, and the card says so.
  const effective: LeagueSelection = selection ?? { offices: [], teams: [] }

  const league = useMemo(
    () => buildLeague(model.ranked, effective, floor, dimension),
    [model.ranked, effective, floor, dimension],
  )
  // What is ACTUALLY ranked, whether it was asked for or defaulted. The chips
  // read their on/off state from this, so a chip is lit exactly when its group
  // is in the table -- the two could previously drift, which is what let a
  // populated selector sit above an empty league.
  const shown = useMemo(() => new Set(league.table.map((s) => `${s.dimension}:${s.name}`)), [league.table])

  // The bump is capped at six lines, because a seventh generated hue is
  // indistinguishable from an existing one under colour-vision deficiency.
  // The table below carries the rest, so nothing is lost — only the lines are.
  const bumpGroups = league.table.slice(0, MAX_SERIES)
  const bump = useMemo(() => buildBump(model.ranked, bumpGroups, floor), [model.ranked, bumpGroups, floor])
  // Whole weeks of heartbeat the WINDOW carries. Below two, `buildBump`
  // returns nothing at all and the card prints why rather than drawing a
  // one-column "season" — see the note there.
  const weeks = weeksAvailable(model)

  /** Toggling starts from WHAT IS ON SCREEN, not from the raw selection, so
   *  the first click on a defaulted league removes one group instead of
   *  selecting one out of an empty set. */
  function toggle(kind: 'offices' | 'teams', name: string) {
    const next: LeagueSelection = {
      offices: league.table.filter((s) => s.dimension === 'office').map((s) => s.name),
      teams: league.table.filter((s) => s.dimension === 'team').map((s) => s.name),
    }
    const list = next[kind]
    const at = list.indexOf(name)
    if (at >= 0) list.splice(at, 1)
    else list.push(name)
    setSelection(next)
  }

  const belowFloor = league.table.filter((s) => s.thin)

  /* THE SELECTOR IS ITS OWN TAB, and the league is the landing content.
     Previously the view opened on the CONTROLS — a dimension picker, a floor
     slider, a pooling toggle and two rows of chips — and the standings, which
     are what anybody came for, were a full screen below them. A selector is
     what you use once; a table is what you read. */
  if (tab === 'select') {
    return (
      <>
      <Card
        title="Who is playing"
        sub="Groups are selectable across both dimensions at once, so 'Sydney versus IT' is a legal fixture. A person can appear on two sides in a mixed comparison, and the view says so rather than silently double counting."
        note={
          league.overlaps.length > 0 ? (
            <>
              <b>These sides are not disjoint.</b>{' '}
              {league.overlaps
                .slice(0, 4)
                .map((o) => `${o.a} and ${o.b} share ${o.shared} ${o.shared === 1 ? 'person' : 'people'}`)
                .join('; ')}
              . A person in both is counted on both sides, so the columns do not add up to the
              platform and are not meant to.
            </>
          ) : (
            'Every selected group is in one dimension, so the sides are disjoint by construction: a person has one office and one team.'
          )
        }
      >
        <div className="split">
          <div>
            <p className="dh">Dimension</p>
            <div className="metricpick" role="group" aria-label="Dimension">
              {(['office', 'team', 'both'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  data-on={dimension === d ? '1' : '0'}
                  onClick={() => {
                    setDimension(d)
                    setSelection(null)
                  }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="dh">Fairness rule</p>
            <label className="csub" style={{ display: 'block' }}>
              A group needs{' '}
              <input
                type="range"
                min={1}
                max={8}
                value={floor}
                aria-label="Contest floor"
                onChange={(e) => setFloor(Number(e.target.value))}
                style={{ verticalAlign: 'middle', width: 140 }}
              />{' '}
              <b>{floor}</b> active people to field a side and move the shield.
            </label>
            <p className="csub">
              This is a fairness and noise rule, not a privacy one. Every office and every team is
              shown at every setting, with its <b>n</b> printed. What the number changes is who can
              be fielded — because a one-person side&rsquo;s score is one person&rsquo;s week, and
              making that a company-wide scoreline turns that person into the reason their side
              lost.
            </p>
            <div className="metricpick" style={{ marginTop: 8 }}>
              <button type="button" data-on={pooled ? '0' : '1'} onClick={() => setPooled(false)}>
                Show every group
              </button>
              <button type="button" data-on={pooled ? '1' : '0'} onClick={() => setPooled(true)}>
                Pool under the floor (the old behaviour)
              </button>
            </div>
          </div>
        </div>

        <p className="csub" style={{ marginTop: 18 }}>
          Every chip starts lit, so a click REMOVES a group rather than adding one. Clearing them
          all returns the default field instead of an empty league — the head-to-head has its own
          two pickers now, so narrowing this selector down to two is no longer how you get a
          fixture.
        </p>
        <p className="dh" style={{ marginTop: 14 }}>
          Offices
        </p>
        <div className="chips">
          {all.allOffices.map((s) => (
            <button
              key={s.name}
              type="button"
              className="selchip"
              data-on={shown.has(`office:${s.name}`) ? '1' : '0'}
              data-thin={s.thin ? '1' : '0'}
              onClick={() => toggle('offices', s.name)}
            >
              {s.name}
              <em>n={s.active}</em>
            </button>
          ))}
        </div>
        <p className="dh" style={{ marginTop: 14 }}>
          Teams
        </p>
        <div className="chips">
          {all.allTeams.map((s) => (
            <button
              key={s.name}
              type="button"
              className="selchip"
              data-on={shown.has(`team:${s.name}`) ? '1' : '0'}
              data-thin={s.thin ? '1' : '0'}
              onClick={() => toggle('teams', s.name)}
            >
              {s.name}
              <em>n={s.active}</em>
            </button>
          ))}
        </div>
      </Card>

      <Card
        title="Not counted for any side"
        sub="Counted and shown, never folded into a group they are not in."
      >
        <p className="csub">
          <span className="selchip" data-on="0">
            Office not set<em>{league.unassignedOffice}</em>
          </span>{' '}
          <span className="selchip" data-on="0">
            Team not set<em>{league.unassignedTeam}</em>
          </span>
        </p>
        <p className="csub" style={{ marginTop: 10 }}>
          A person with no city or department in the directory competes for nobody. Guessing one
          from their state would put them in an office they have never been to in order to make a
          chart look tidier, which is a worse outcome than a chip that says &ldquo;not set&rdquo;.
        </p>
      </Card>
      </>
    )
  }

  if (tab === 'weeks') {
    return (
      <Card
        title="Who won each week"
        sub="Position by week, best at the top. The lines cross where the order changed. This is what the old best-of-three pip strip was trying to say with three dots."
        note={
          // Nothing drawn, nothing to annotate. A note explaining how a chart
          // handles absences, printed under no chart, is the same defect in
          // prose that the chart itself had.
          weeks < 2 ? null : (
            <>
              A week a group could not field the floor is drawn as a <b>gap</b>, not as a loss. A
              side that did not turn up did not lose, and the shield does not move on an absence.
              {league.table.length > MAX_SERIES
                ? ` Six lines are drawn; the other ${league.table.length - MAX_SERIES} selected groups are in the league table, because a seventh generated hue is indistinguishable from an existing one under colour-vision deficiency.`
                : ''}
            </>
          )
        }
      >
        {/* THE WINDOW HAS TO REACH TWO WEEKS OR THERE IS NOTHING TO DRAW. It
            used to floor the week count at one, so a 24H window produced a
            chart headed "Week 1" built from a single day — on a card whose
            entire subject is position CHANGING between weeks. */}
        {weeks < 2 ? (
          <p className="hint">
            NO WEEKLY POSITIONS IN A {model.window.toUpperCase()} WINDOW. It covers{' '}
            {model.days.length === 1 ? 'one calendar day' : `${model.days.length} calendar days`},
            and a bump chart needs at least two whole weeks — with one column the lines cannot
            cross, which is the only thing this chart is for. Pick MO or ALL.
          </p>
        ) : (
          <>
            <BumpChart
              series={bump}
              colors={colors}
              weekLabels={(bump[0]?.points ?? []).map((_, i) => `Week ${i + 1}`)}
            />
            <ChartLegend
              shape="line"
              items={bumpGroups.map((g, i) => ({ label: g.name, color: slotColor(colors, i) }))}
            />
          </>
        )}
      </Card>
    )
  }

  if (tab === 'h2h') {
    return (
      <HeadToHead
        model={model}
        floor={floor}
        offices={all.allOffices}
        teams={all.allTeams}
        pooled={pooled}
      />
    )
  }

  return (
    <>
      <Card
        title="The league"
        sub="Every selected group, scored on tokens per active person. Groups below the contest floor are drawn hatched with their n printed, because a one-person score is one person's week and should not read as a firm number."
        note={
          <>
            {belowFloor.length === 0
              ? 'Every group in this table clears the floor this window.'
              : `${belowFloor.length} of ${league.table.length} groups here are under the floor and cannot be fielded. They are still shown, with their numbers, because nothing on this board can be more revealing than the individual rows already published in the same view.`}
            {league.defaulted ? (
              <>
                {' '}
                Nothing is selected on WHO IS PLAYING, so this is the default field: every{' '}
                {league.table.some((s) => s.dimension === 'office') ? 'office' : 'team'} with
                activity in this window. <b>An empty selection shows the default, never an empty
                table</b> — a league that can render nothing is a league that will.
              </>
            ) : null}
          </>
        }
      >
        {league.table.length === 0 ? (
          <p className="hint">
            NO GROUPS WITH ACTIVITY IN THIS WINDOW. Neither the office nor the team dimension
            resolved a single group, which means the directory has no city and no department for
            anybody who used the platform in this period — not that nobody did. The board itself is
            still this window&rsquo;s.
          </p>
        ) : (
          <>
            <RankedBars
              rows={league.table.map((s) => ({
                label: s.name,
                value: s.score,
                note: `n=${s.active}${s.thin ? ' · thin' : ''}`,
                hatched: s.thin,
              }))}
              colors={colors}
              format={fmtTokens}
              label="Tokens per active person by group"
            />
            <div className="tablewrap" style={{ marginTop: 14 }}>
              <table className="chtable">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Group</th>
                    <th>Dimension</th>
                    <th className="num">Active</th>
                    <th className="num">In the group</th>
                    <th className="num">Tokens / person</th>
                    <th>Series</th>
                  </tr>
                </thead>
                <tbody>
                  {league.table.map((s, i) => (
                    <tr key={`${s.dimension}-${s.name}`} data-thin={s.thin ? '1' : '0'}>
                      <td className="num">{i + 1}</td>
                      <td>
                        <b>{s.name}</b>
                      </td>
                      <td>{s.dimension}</td>
                      <td className="num">{s.active}</td>
                      <td className="num">{s.members}</td>
                      <td className="num">{s.scoreText}</td>
                      <td>{s.fieldable ? 'can field a side' : 'not fielded'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <p className="rvfoot">
        Measured per active person so a bigger group cannot win on headcount, and so no single
        person decides a side. Volume is not productivity: someone handling fewer, harder pieces of
        work is not behind.
      </p>
    </>
  )
}

/**
 * THE HEAD-TO-HEAD, WITH ITS OWN SELECTION.
 *
 * What this replaces: a tab that computed its fixture from the LEAGUE's
 * multi-group selection and, whenever that named anything other than exactly
 * two groups, rendered one sentence telling the viewer to go to a different
 * tab and change something. The default selection is every office, which is
 * four groups, so the tab's normal state was an instruction to leave it. On
 * the live board it read, in Maroun's word, as "nothing".
 *
 * Two pickers, each holding every office and every team, so the question
 * "this team against that team" — or "Sydney against Underwriting" — is asked
 * here rather than assembled somewhere else. The league keeps the chips for
 * what they are good at, which is ranking many groups at once.
 *
 * The five safety rules are unchanged and are the reason this arena is
 * allowed to exist at all: no individual appears, the score is per active
 * person, both sides keep full colour whatever the score, the gap is phrased
 * from the trailing side and is always positive, and a fixture that cannot
 * move the shield says so rather than hiding.
 */
function HeadToHead({
  model,
  floor,
  offices,
  teams,
  pooled,
}: {
  model: BoardModel
  floor: number
  offices: RivalryStanding[]
  teams: RivalryStanding[]
  pooled: boolean
}) {
  const fallback = useMemo(() => defaultFixture(offices, teams), [offices, teams])
  const [picked, setPicked] = useState<{ a: FixtureSide; b: FixtureSide } | null>(null)

  // The picked sides survive a window change only while they still exist. A
  // group that had activity last month and none this week drops out of the
  // standings entirely, and a picker pointing at it would render an empty
  // arena -- the same class of defect this component exists to remove.
  const catalogue = useMemo(() => [...offices, ...teams], [offices, teams])
  const alive = (s: FixtureSide | undefined) =>
    s !== undefined && catalogue.some((c) => c.dimension === s.dimension && c.name === s.name)
  const sides = picked && alive(picked.a) && alive(picked.b) ? picked : fallback

  const fixture = useMemo(
    () => (sides ? buildFixture(model.ranked, sides.a, sides.b, model.series, floor) : null),
    [model.ranked, model.series, sides, floor],
  )

  // The old pooling behaviour, kept switchable from the selector tab so the
  // cost of the pre-31-Aug rule stays inspectable. Scoped to the dimensions
  // actually in play: pooling teams into an office fixture would be answering
  // a question nobody asked.
  const pool = useMemo(() => {
    if (!pooled || !sides) return null
    const dims = new Set([sides.a.dimension, sides.b.dimension])
    const below = catalogue.filter((s) => dims.has(s.dimension) && s.thin)
    const count = below.reduce((n, s) => n + s.active, 0)
    const tokens = below.reduce((n, s) => n + s.score * s.active, 0)
    return {
      count,
      groups: below.length,
      scoreText: count >= floor && count > 0 ? fmtTokens(Math.round(tokens / count)) : null,
    }
  }, [pooled, sides, catalogue, floor])

  function pick(which: 'a' | 'b', raw: string) {
    const [dimension, ...rest] = raw.split(':')
    const next: FixtureSide = { dimension: dimension === 'team' ? 'team' : 'office', name: rest.join(':') }
    const base = sides ?? fallback
    if (!base) return
    setPicked(which === 'a' ? { a: next, b: base.b } : { a: base.a, b: next })
  }

  const options = (
    <>
      <optgroup label="Offices">
        {offices.map((s) => (
          <option key={`office:${s.name}`} value={`office:${s.name}`}>
            {s.name} (n={s.active})
          </option>
        ))}
      </optgroup>
      <optgroup label="Teams">
        {teams.map((s) => (
          <option key={`team:${s.name}`} value={`team:${s.name}`}>
            {s.name} (n={s.active})
          </option>
        ))}
      </optgroup>
    </>
  )

  if (!sides || catalogue.length < 2) {
    return (
      <Card title="Head to head" sub="Two sides, chosen here.">
        <p className="hint">
          A FIXTURE NEEDS TWO GROUPS AND THIS WINDOW HAS {catalogue.length}. Nothing is being
          withheld — there is only one group with any activity in it. Pick a longer window.
        </p>
      </Card>
    )
  }

  const mixed = sides.a.dimension !== sides.b.dimension
  const same = sameSide(sides.a, sides.b)

  return (
    <Card
      title="Head to head"
      sub="Two sides, chosen here rather than inherited from the league. Any office against any team; both keep full colour whatever the score, and the gap is always phrased from the trailing side and always positive."
      note={
        fixture === null
          ? null
          : fixture.countsForShield
            ? fixture.stored
              ? 'A stored result: this is a record of finished weeks, not an estimate.'
              : `Recent form, split into three equal blocks of this ${model.window.toUpperCase()} window — not a stored result, and the view says so rather than dressing an estimate as a record.`
            : `This fixture cannot move the shield: that needs ${floor} active people a side. The comparison is still real; the shield is not.`
      }
    >
      <div className="fixpick">
        <div>
          <label htmlFor="h2h-a">Side A</label>
          <select
            id="h2h-a"
            value={`${sides.a.dimension}:${sides.a.name}`}
            onChange={(e) => pick('a', e.target.value)}
          >
            {options}
          </select>
        </div>
        <div>
          <label htmlFor="h2h-b">Side B</label>
          <select
            id="h2h-b"
            value={`${sides.b.dimension}:${sides.b.name}`}
            onChange={(e) => pick('b', e.target.value)}
          >
            {options}
          </select>
        </div>
      </div>

      {same || fixture === null ? (
        <p className="hint" style={{ marginTop: 14 }}>
          PICK TWO DIFFERENT SIDES. A group cannot play itself, and the view says so rather than
          drawing a fixture that is level by construction.
        </p>
      ) : (
        <>
          <div className="split" style={{ marginTop: 16 }}>
            <div>
              <p className="dh">
                {fixture.a.name} <span className="chnote">· {sides.a.dimension}</span>
              </p>
              <div className="kpi">
                <div className="k">Tokens per active person</div>
                <div className="v">{fixture.a.scoreText}</div>
                <div className="d">
                  {fixture.a.active} active
                  {fixture.leader === 'b' ? ` · ${fixture.gapText}` : ''}
                </div>
              </div>
            </div>
            <div>
              <p className="dh">
                {fixture.b.name} <span className="chnote">· {sides.b.dimension}</span>
              </p>
              <div className="kpi">
                <div className="k">Tokens per active person</div>
                <div className="v">{fixture.b.scoreText}</div>
                <div className="d">
                  {fixture.b.active} active
                  {fixture.leader === 'a' ? ` · ${fixture.gapText}` : ''}
                </div>
              </div>
            </div>
          </div>

          {/* A MIXED FIXTURE DECLARES ITS OVERLAP, ALWAYS. Two offices are
              disjoint and so are two teams, but an office and a team are not,
              and a scoreline that let a reader assume otherwise would be
              inviting them to add two columns that share people. */}
          {mixed ? (
            <p className="csub" style={{ marginTop: 14 }}>
              <b>These sides are not disjoint.</b>{' '}
              {fixture.overlap === 0
                ? `No one is in both ${fixture.a.name} and ${fixture.b.name} this window, so the two columns happen to be disjoint — but they are not disjoint by construction, which is why this line is here at all.`
                : `${fixture.overlap} ${fixture.overlap === 1 ? 'person is' : 'people are'} in both ${fixture.a.name} and ${fixture.b.name}, and ${fixture.overlap === 1 ? 'is' : 'are'} counted on both sides. The two scores do not add up to anything.`}
            </p>
          ) : null}

          <p className="csub" style={{ marginTop: mixed ? 8 : 14 }}>
            {fixture.holder
              ? `${fixture.holder} hold the shield. It moves when a side takes two periods of three.`
              : 'Nobody holds the shield yet — it takes two periods of three.'}
            {mixed
              ? ' A mixed fixture is always provisional: the series table stores one grouping kind per pairing, and an office-against-team result has never been one of them.'
              : ''}
          </p>

          {pool ? (
            <p className="csub">
              Pooled view: {pool.count} {pool.count === 1 ? 'person' : 'people'} across {pool.groups}{' '}
              {pool.groups === 1 ? 'group' : 'groups'} under the floor
              {pool.scoreText ? `, ${pool.scoreText} per person` : ', score withheld'}. This is the
              OLD behaviour, kept switchable so you can see exactly what it cost.
            </p>
          ) : null}
        </>
      )}
    </Card>
  )
}

/* ==========================================================================
 * 2b. TEAM WARS as a race track
 * ======================================================================== */

/** Milliseconds a day holds when the track is playing. Slow enough to read a
 *  position change, fast enough that a month does not outlast the viewer. */
const PLAY_STEP_MS = 420

/**
 * A field of runners with a timeline under it.
 *
 * THE METAPHOR IS NOT ALLOWED TO COST READABILITY, and three decisions do
 * that work. The scale is argued where it is implemented, in `RaceTrack` —
 * read that first. Here: the lanes never reorder under the scrub, every lane
 * carries its own position number so an overtake reads as a number changing
 * as well as a mark moving, and `prefers-reduced-motion` removes the
 * animation rather than merely slowing it. A viewer who has asked their
 * operating system not to animate things is not asking for a gentler
 * animation.
 *
 * The slider is a native `input[type=range]`, so arrow keys, Home, End and
 * Page Up/Down all work with nothing written here, which is the whole reason
 * it is not a custom control.
 */
function RaceTrackPanel({
  model,
  theme,
  reducedMotion,
}: {
  model: BoardModel
  theme: ArcadeTheme
  reducedMotion: boolean
}) {
  const runners = useMemo(() => buildTrack(model.ranked, 'team'), [model.ranked])
  const span = model.days.length
  const [at, setAt] = useState(Math.max(0, span - 1))
  const [playing, setPlaying] = useState(false)

  // A window change replaces the day axis under the scrubber. Clamp rather
  // than leave the handle pointing past the end of the series.
  useEffect(() => {
    setAt(Math.max(0, span - 1))
    setPlaying(false)
  }, [span])

  useEffect(() => {
    if (!playing || reducedMotion) return
    const timer = window.setInterval(() => {
      setAt((cur) => {
        if (cur >= span - 1) {
          setPlaying(false)
          return cur
        }
        return cur + 1
      })
    }, PLAY_STEP_MS)
    return () => window.clearInterval(timer)
  }, [playing, reducedMotion, span])

  if (!supportsDailySeries(model) || runners.length === 0) {
    return (
      <Card title="The race" sub="Departments as a field of runners, scrubbed over the window.">
        <WindowTooShort form="RACE" model={model} />
      </Card>
    )
  }

  const day = model.days[Math.min(at, span - 1)] ?? ''
  const lanes = runners.map((r) => ({
    name: r.name,
    values: r.values,
    active: r.active,
    members: r.members,
  }))
  const leader = [...lanes].sort(
    (a, b) => (b.values[at] ?? 0) - (a.values[at] ?? 0),
  )[0]

  return (
    <Card
      title="The race · cumulative tokens by department"
      sub="Position along a lane is the running token total to the day on the scrubber. The lanes are ordered by where each department finishes and never reshuffle, so a runner you are following stays on its own row; the number in front of each name is its position at the moment you are looking at."
      note={
        <>
          <b>The track is linear and the finish line is the leader&rsquo;s total at this
          moment</b>, so the field&rsquo;s spread is the real spread. Departments differ by tens of
          times on this platform, which puts most of the field near the start line — that is the
          finding, not a fault in the chart, and every lane prints its own figure so the tail stays
          readable as numbers. <b>Tokens, not dollars:</b> there is no daily spend series anywhere
          on the wire and spend cannot be derived from tokens, because the price differs per model.
          The dollar totals on THE GRID tab are the same window, a different measure.
          <TruncationNote model={model} />
        </>
      }
    >
      <div className="scrub">
        {/* Play is a convenience, not the interface. Suppressed entirely under
            prefers-reduced-motion: an autoplaying chart is exactly the thing
            that setting exists to switch off. */}
        {reducedMotion ? null : (
          <button type="button" onClick={() => setPlaying((p) => !p)}>
            {playing ? 'Pause' : at >= span - 1 ? 'Replay' : 'Play'}
          </button>
        )}
        <input
          type="range"
          min={0}
          max={Math.max(0, span - 1)}
          step={1}
          value={at}
          aria-label="Day on the race track"
          aria-valuetext={day}
          onChange={(e) => {
            setPlaying(false)
            setAt(Number(e.target.value))
          }}
        />
        <span className="day">{day}</span>
      </div>
      {/* `key` on the scrubbed day is deliberate: it forces a fresh paint of
          the marks rather than a CSS transition between positions, so nothing
          animates for a viewer who asked for no animation. */}
      <RaceTrack lanes={lanes} at={at} colors={theme.chartColors} format={fmtTokens} label="Cumulative tokens by department" />
      <p className="csub" style={{ marginTop: 10 }}>
        {leader
          ? `${leader.name} lead at ${day} on ${fmtTokens(leader.values[at] ?? 0)} cumulative tokens.`
          : ''}{' '}
        Cumulative totals, so a lane can only move forward: what changes is the order and the
        spacing. Volume is not productivity — a department handling fewer, harder pieces of work is
        not behind.
      </p>
      <div className="tablewrap" style={{ marginTop: 14 }}>
        <table className="chtable narrow">
          <thead>
            <tr>
              <th>#</th>
              <th>Department</th>
              <th className="num">Active</th>
              <th className="num">In the team</th>
              <th className="num">Tokens to {day}</th>
              <th className="num">Tokens over the window</th>
            </tr>
          </thead>
          <tbody>
            {[...lanes]
              .sort((a, b) => (b.values[at] ?? 0) - (a.values[at] ?? 0))
              .map((l, i) => (
                <tr key={l.name}>
                  <td className="num">{i + 1}</td>
                  <td>
                    <b>{l.name}</b>
                  </td>
                  <td className="num">{l.active}</td>
                  <td className="num">{l.members}</td>
                  <td className="num">{fmtTokens(l.values[at] ?? 0)}</td>
                  <td className="num">{fmtTokens(l.values[l.values.length - 1] ?? 0)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

/** The Team Wars tab strip's second panel. Exported so the page can render it
 *  beside the grid without importing the track chart itself. */
export function TeamWarsRace({
  model,
  theme,
  reducedMotion,
}: {
  model: BoardModel
  theme: ArcadeTheme
  reducedMotion: boolean
}) {
  return <RaceTrackPanel model={model} theme={theme} reducedMotion={reducedMotion} />
}

/* ==========================================================================
 * 3. BIG SPENDERS enrichment
 * ======================================================================== */

type TrendMetric = 'tokens' | 'active'

/**
 * The panels that sit above the ranked board: the race, the trend, the
 * enriched table and the group charts.
 *
 * WHAT THE PAYLOAD ACTUALLY CARRIES, and it is less than the mock assumed.
 * Every player has 21 daily TOKEN buckets (the row heartbeat) and nothing
 * else per day — there is no daily SPEND series on the wire, and spend cannot
 * be derived from tokens because the price differs per model. So the race is
 * cumulative TOKENS and says so, and the trend offers the two measures that
 * are genuinely daily. Inventing a daily spend line from a monthly total is
 * exactly the kind of confident wrong number this board is trying to stop
 * printing.
 */
export function SpendersPanels({
  model,
  theme,
  tab,
  onSelectPlayer,
}: {
  model: BoardModel
  theme: ArcadeTheme
  /** Which sub-tab is showing. See `arcade/tabs.tsx`. */
  tab: string
  onSelectPlayer: (id: string) => void
}) {
  const colors = theme.chartColors
  const [trend, setTrend] = useState<TrendMetric>('tokens')

  const race = useMemo(() => buildRace(model.ranked, MAX_SERIES - 1), [model.ranked])
  const raceSeries = race.series.map((s, i) => ({
    name: s.name,
    values: s.values,
    color: s.id === null ? 'var(--a-ink-faint)' : slotColor(colors, i),
    muted: s.id === null,
  }))

  // THE AXIS COMES FROM THE MODEL, WHICH GOT IT FROM THE SERVER, WHICH KNOWS
  // THE WINDOW. It used to be rebuilt here from `Date.now()` and the spark
  // length, and that is precisely how a 24H window drew three weeks: the only
  // component that knew which days the buckets were was not the one labelling
  // them. `MM-DD` for the ticks; the tooltip carries the full date.
  const span = model.days.length
  const dayLabels = useMemo(() => model.days.map((d) => d.slice(5)), [model.days])
  const daily = useMemo(() => {
    const tokens: number[] = []
    const active: number[] = []
    for (let i = 0; i < span; i++) {
      let t = 0
      let a = 0
      for (const p of model.ranked) {
        const v = p.spark[i] ?? 0
        t += v
        if (v > 0) a += 1
      }
      tokens.push(t)
      active.push(a)
    }
    return { tokens, active }
  }, [model.ranked, span])
  const hasDaily = supportsDailySeries(model)

  // The adoption headline. Twelve of twenty-two measured exactly one day on
  // 30 Aug 2026, and the board has never said it out loud.
  const oneDayOnly = model.ranked.filter((p) => p.activeDays === 1).length
  const returning = model.ranked.filter((p) => p.activeDays >= 2).length
  const withQuality = model.ranked.filter((p) => p.requests > 0)
  const failRate = model.quality.requests > 0 ? model.quality.failed / model.quality.requests : null
  const cacheRate = model.quality.cacheable > 0 ? model.quality.cacheHits / model.quality.cacheable : null

  // The built-in / MCP split, now that it is measurable. `families` is empty
  // when the warehouse could not be read, which is NOT the same as a zero.
  const familyCalls = (f: ToolFamily) => model.mcp.families.find((x) => x.family === f)?.calls ?? 0
  const codeCalls = familyCalls('code')
  const mcpCalls = familyCalls('mcp')
  const toolCallsKnown = model.mcp.available && model.mcp.families.length > 0

  const usage = model.usage
  const agentShare =
    usage.available && usage.conversations > 0 ? usage.agentConversations / usage.conversations : null

  const offices = groupChart(model.ranked, 'office', model.contestFloor)
  const teams = groupChart(model.ranked, 'team', model.contestFloor)

  if (tab === 'summary') {
    return (
      <>
        <div className="kpis">
          <Kpi
            label="Active people"
            value={fmtInt(model.org.players)}
            detail={`${returning} came back on a second day`}
          />
          {/* PROMOTED FROM A SUBTITLE TO A TILE. It was already computed and
              already true, and it was hiding in the detail line of the tile
              beside it — which is the wrong place for the single most
              actionable number a CTO can read off this board. Adoption is not
              headcount; it is whether anybody returned. */}
          <Kpi
            label="Used it once, ever"
            value={fmtInt(oneDayOnly)}
            detail={`of ${model.org.players} — active on exactly one day`}
          />
          <Kpi label="Cost per active person" value={model.org.costPerPerson.text} detail="platform spend ÷ active" />
          <Kpi label="Tokens this window" value={model.org.totalTokensText} detail={`across ${model.org.players} people`} />
          <Kpi
            label="Request failures"
            value={failRate === null ? '—' : fmtPct(failRate)}
            detail={
              model.quality.available
                ? `${fmtInt(model.quality.failed)} of ${fmtInt(model.quality.requests)} requests`
                : 'gateway status could not be read'
            }
          />
          <Kpi
            label="Answer latency p90"
            value={model.quality.p90Ms === null ? '—' : fmtMs(model.quality.p90Ms)}
            detail={model.quality.p50Ms === null ? 'from the gateway' : `median ${fmtMs(model.quality.p50Ms)}`}
          />
          <Kpi
            label="Prompt cache hit rate"
            value={cacheRate === null ? '—' : fmtPct(cacheRate)}
            detail="of successful requests"
          />
          <Kpi label="Tool calls" value={fmtInt(model.mcp.totalCalls)} detail={`${model.mcp.totalPeople} people used one`} />
          {/* NEW, and only sayable since the `(unknown)` bucket was taken
              apart. The code interpreter is not an MCP server and was counted
              as one; this tile is the headline of that finding. The share is
              of CALLS and the Tools view states, beside every bar, why calls
              are not comparable work. */}
          <Kpi
            label="Code interpreter share"
            value={toolCallsKnown && model.mcp.totalCalls > 0 ? fmtPct(codeCalls / model.mcp.totalCalls) : '—'}
            detail={
              toolCallsKnown
                ? `${fmtInt(codeCalls)} calls against ${fmtInt(mcpCalls)} MCP`
                : 'tool tables could not be read'
            }
          />
          {/* The existing `daily_feature_usage.agents` counter says 100% and
              always has: it tests `agent_id` for truthiness and `agent_id` is
              set on every conversation. This counts `agent_<nanoid>` only. */}
          <Kpi
            label="Agent vs plain chat"
            value={agentShare === null ? '—' : fmtPct(agentShare)}
            detail={
              usage.available
                ? `${fmtInt(usage.agentConversations)} of ${fmtInt(usage.conversations)} conversations`
                : 'LibreChat could not be read'
            }
          />
          <Kpi label="Live streaks" value={fmtInt(model.org.liveStreaks)} detail="three or more consecutive days" />
        </div>

        <TopicTrendPanel model={model} theme={theme} />
      </>
    )
  }

  if (tab === 'heatmap') {
    return <HeatmapPanel model={model} theme={theme} />
  }

  if (tab === 'race') {
    return (
      <Card
        title="The race · cumulative tokens, day by day"
        sub="Who was in front, and when it changed hands. The line is a running total, so it can only go up; what moves is the order."
        note={
          // A note about a chart that is not on screen is the same defect in
          // prose that the chart itself had: text describing something the
          // window does not contain. When there is no race, there is nothing
          // to annotate.
          hasDaily ? (
            <>
              {race.leadChanges.length === 0
                ? 'The lead did not change hands in this window. The markers on this chart are computed from the series, so an empty chart means an empty result rather than a missing annotation.'
                : `${race.leadChanges.length} lead change${race.leadChanges.length === 1 ? '' : 's'}, computed from the series rather than annotated by hand.`}{' '}
              Five people are drawn; the rest are pooled into one grey line so the chart never
              generates a colour it cannot keep distinct. <b>That pooling is a charting decision,
              not a privacy one</b> — every pooled person is named on the BOARD tab.
              <TruncationNote model={model} />
            </>
          ) : null
        }
      >
        {hasDaily ? (
          <>
            <LineChart
              series={raceSeries}
              xLabels={dayLabels}
              format={fmtTokens}
              markers={race.leadChanges.map((c) => ({ at: c.at, text: `${c.to} takes the lead` }))}
              label="Cumulative tokens per person over the window"
            />
            <ChartLegend shape="line" items={raceSeries.map((s) => ({ label: s.name, color: s.color }))} />
          </>
        ) : (
          <WindowTooShort form="RACE" model={model} />
        )}
      </Card>
    )
  }

  if (tab === 'trend') {
    return (
      <Card
        title="Platform trend"
        sub="One measure at a time on one axis. The metric picker swaps the series rather than adding a second scale."
        right={
          <div className="metricpick" role="group" aria-label="Trend metric">
            <button type="button" data-on={trend === 'tokens' ? '1' : '0'} onClick={() => setTrend('tokens')}>
              Tokens / day
            </button>
            <button type="button" data-on={trend === 'active' ? '1' : '0'} onClick={() => setTrend('active')}>
              Active people
            </button>
          </div>
        }
        note={
          hasDaily ? (
            <>
              Two measures, not eight, and the reason is the payload: every player carries daily
              TOKEN buckets and nothing else per day. There is no daily spend series on the wire,
              and spend cannot be derived from tokens because the price differs per model — so a
              &ldquo;spend per day&rdquo; line here would be a number this board made up.
              <TruncationNote model={model} />
            </>
          ) : null
        }
      >
        {hasDaily ? (
          <LineChart
            series={[
              {
                name: trend === 'tokens' ? 'Tokens' : 'Active people',
                values: trend === 'tokens' ? daily.tokens : daily.active,
                color: slotColor(colors, 0),
              },
            ]}
            xLabels={dayLabels}
            format={trend === 'tokens' ? fmtTokens : fmtInt}
            label={trend === 'tokens' ? 'Tokens per day' : 'Active people per day'}
          />
        ) : (
          <WindowTooShort form="TREND" model={model} />
        )}
      </Card>
    )
  }

  if (tab === 'groups') {
    return (
      <Card
        title="Groups below the floor · now shown"
        sub="Every office and every team, with its active count stated. Groups under the contest floor keep a hatched fill and a printed n, so a small number is never read as a firm one."
        note={
          <>
            Under the old rule a one-person office was pooled into &ldquo;Other offices&rdquo; and
            its score withheld. <b>That withheld nothing:</b> the same person is named on the BOARD
            tab, with their office, their spend and their streak, in the same view and to the same
            audience. The floor was hiding a comparison, not an identity.
          </>
        }
      >
        <div className="split">
          <div>
            <p className="dh">By office</p>
            <RankedBars rows={offices} colors={colors} format={fmtTokens} label="Tokens per active person by office" />
          </div>
          <div>
            <p className="dh">By team</p>
            <RankedBars rows={teams} colors={colors} format={fmtTokens} label="Tokens per active person by team" />
          </div>
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card
        title="The board"
        sub="Every active person, no floor. Depth, breadth and reach sit beside the money, because the money on its own says only who typed most."
        note={
          <>
            <b>Days active</b> is the column the board never had.{' '}
            {oneDayOnly} of {model.ranked.length} people here have used CHU-GPT on exactly one day
            in this window, which is the honest adoption headline and is not visible anywhere in a
            spend ranking.
            {model.quality.available ? '' : ' Failure rate and latency could not be read this window.'}{' '}
            <b>TRAJECTORY describes the line beside it, never the person.</b> It is derived from
            the daily heartbeat, days active, tenure and streak — and from nothing else: rank,
            tokens and spend are not inputs, so it cannot be comparative and there is no phrase in
            it that calls anybody out. Volume is not productivity: someone handling fewer, harder
            pieces of work is not behind, and no sentence in this column says otherwise.
          </>
        }
      >
        <div className="tablewrap">
          <table className="chtable wide">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Team</th>
                <th>Office</th>
                <th className="num">Spend</th>
                <th className="num">Tokens</th>
                <th className="num">Days active</th>
                <th className="num">Streak</th>
                <th className="num">Models</th>
                <th className="num">Topics</th>
                <th className="num">Requests</th>
                <th className="num">Failures</th>
                <th className="num">p90</th>
                {/* Was headed "What for", which named the taxonomy and drew the
                    daily token heartbeat. One of the two was wrong and it was
                    the header. */}
                <th>Daily activity</th>
                <th>Trajectory</th>
              </tr>
            </thead>
            <tbody>
              {model.ranked.map((p) => (
                <tr
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`${p.name} profile`}
                  onClick={() => onSelectPlayer(p.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectPlayer(p.id)
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="num">{p.rank}</td>
                  <td>
                    <b>{p.name}</b>
                  </td>
                  <td>{p.department}</td>
                  <td>{p.office}</td>
                  {/* Through the seam's own `Figure`, which is the only place a
                      dollar sign is produced anywhere in the arcade. */}
                  <td className="num" data-withheld={p.cost.withheld ? '1' : '0'}>
                    {p.cost.text}
                  </td>
                  <td className="num">{p.tokensText}</td>
                  <td className="num">
                    <b>{p.activeDays}</b>
                  </td>
                  <td className="num">{p.streak === 0 ? '—' : p.streak}</td>
                  <td className="num">{p.models}</td>
                  <td className="num">{p.topicsUsed}</td>
                  <td className="num">{p.requests === 0 ? '—' : fmtInt(p.requests)}</td>
                  {/* Null, not 0%, when there were no requests to fail: "no
                      failures" and "no data" are different claims. */}
                  <td className="num">{p.failRate === null ? '—' : fmtPct(p.failRate)}</td>
                  <td className="num">{p.p90Ms === null ? '—' : fmtMs(p.p90Ms)}</td>
                  <td style={{ minWidth: 160 }}>
                    <SmallMultiple
                      values={p.spark}
                      color={slotColor(colors, 0)}
                      label={`${p.name} daily activity`}
                    />
                  </td>
                  {/* A caption on the sparkline to its left, in dim ink at
                      body size — never a verdict with emphasis. See
                      `outlookFor`, where the full phrase list lives. */}
                  <td className="traj">{outlookFor(p).text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {withQuality.length === 0 && model.quality.available ? (
          <p className="csub" style={{ marginTop: 10 }}>
            No per-person request rows matched this window.
          </p>
        ) : null}
      </Card>
    </>
  )
}

/* ==========================================================================
 * 3c. THE ACTIVITY HEATMAP — people against the things they reached for
 * ======================================================================== */

/** How many rows the heatmap draws before it folds the tail. At 22 people the
 *  cap never bites; it exists so the panel degrades sensibly rather than
 *  drawing two hundred 18px rows the day the platform grows. */
const HEAT_ROWS = 40
/** Column cap, for the same reason. Past this the tail is pooled into one
 *  "other tools" column, which is a count and not a category. */
const HEAT_COLS = 14

type HeatAxis = 'server' | 'tool'

/**
 * WHAT THIS CAN AND CANNOT SHOW, decided by the warehouse grain rather than
 * by taste.
 *
 * `daily_mcp_tool_usage` is one row per day, per server, per tool, per person,
 * carrying a call count and a distinct-conversation count. So PERSON × SERVER
 * and PERSON × TOOL are both exact: calls sum freely in every direction, and
 * the panel sums nothing else — the conversation count at that grain would
 * double count a thread that used four tools, and is not on the wire here at
 * all.
 *
 * TOOL CHAINS ARE NOT SHOWN, and cannot be. A chain is an ORDER of calls
 * inside one conversation, and the warehouse stores no conversation
 * identifier anywhere: the nightly job reduces the set of conversation ids to
 * its size inside the function that reads them, and no column downstream
 * carries one. Answering "what follows what" needs a new table at (day,
 * conversation) grain with a sequence on it. Mongo still holds the ordering
 * (`conversationId`, `createdAt`, `parentMessageId` are on every message), so
 * it is a schema change and a nightly job, not new instrumentation — but it
 * is a schema change, and this panel says so instead of drawing an arrow it
 * cannot support.
 */
export function HeatmapPanel({ model, theme }: { model: BoardModel; theme: ArcadeTheme }) {
  const [axis, setAxis] = useState<HeatAxis>('server')
  const [asTable, setAsTable] = useState(false)
  const cells = model.mcp.people

  const grid = useMemo(() => {
    // Sum to the chosen column grain. Calls, and only calls: this is the one
    // measure on this table that sums in every direction.
    // Keyed on a NUL join, never a space: a display name has spaces in it and
    // a server name can too, so any printable separator makes the key
    // ambiguous and the grid silently wrong for somebody.
    const totals = new Map<string, { row: string; col: string; value: number }>()
    const rowTotal = new Map<string, number>()
    const colTotal = new Map<string, number>()
    for (const c of cells) {
      const col = axis === 'server' ? c.server : c.tool
      const key = `${c.name}\u0000${col}`
      const cur = totals.get(key)
      if (cur) cur.value += c.calls
      else totals.set(key, { row: c.name, col, value: c.calls })
      rowTotal.set(c.name, (rowTotal.get(c.name) ?? 0) + c.calls)
      colTotal.set(col, (colTotal.get(col) ?? 0) + c.calls)
    }
    // SERIATION, and it is not alphabetical on purpose. Rows and columns are
    // both ordered by their own marginal total, which pushes the mass to the
    // top left and makes the block structure — who reaches for what — visible
    // as a shape rather than as a list you have to read cell by cell.
    const rows = [...rowTotal.entries()].sort((a, b) => b[1] - a[1]).slice(0, HEAT_ROWS).map(([n]) => n)
    const cols = [...colTotal.entries()].sort((a, b) => b[1] - a[1]).slice(0, HEAT_COLS).map(([n]) => n)
    const keepCol = new Set(cols)
    const keepRow = new Set(rows)
    const out = [...totals.values()].filter((c) => keepCol.has(c.col) && keepRow.has(c.row))
    return { rows, cols, cells: out, hiddenCols: Math.max(0, colTotal.size - cols.length) }
  }, [cells, axis])

  if (!model.mcp.available) {
    return (
      <Card title="Activity heatmap" sub="People against the servers and tools they reached for.">
        <p className="hint">
          THE TOOL TABLES COULD NOT BE READ. That is a different claim from &ldquo;nobody used a
          tool&rdquo;, and this panel will not draw a confident zero over a table it never saw.
        </p>
      </Card>
    )
  }

  return (
    <Card
      title="Activity heatmap"
      sub="One cell per person per server. Darker is more tool calls; an empty cell is nobody reaching for that server at all, which is a real answer and not a gap."
      right={
        <div className="metricpick" role="group" aria-label="Heatmap columns">
          <button type="button" data-on={axis === 'server' ? '1' : '0'} onClick={() => setAxis('server')}>
            By server
          </button>
          <button type="button" data-on={axis === 'tool' ? '1' : '0'} onClick={() => setAxis('tool')}>
            By tool
          </button>
        </div>
      }
      note={
        <>
          <b>Tool CHAINS are not shown, and are not a rendering choice.</b> The warehouse records a
          call count and a distinct-conversation count per day, server, tool and person, and no
          conversation identifier at all — so &ldquo;this tool, then that one&rdquo; is not a
          question the tables can answer. It would take a new table at conversation grain with a
          sequence on it; the ordering still exists in LibreChat, so it is a schema change and a
          nightly job rather than new instrumentation, and it is not in this round.{' '}
          <b>Rows and columns are ordered by their own totals, not alphabetically</b>, so the shape
          of who-uses-what is visible rather than scattered.
          {grid.hiddenCols > 0
            ? ` ${grid.hiddenCols} further ${axis === 'server' ? 'servers' : 'tools'} are outside the ${HEAT_COLS}-column cap and are in the table view.`
            : ''}{' '}
          At this size — {grid.rows.length} people against {grid.cols.length}{' '}
          {axis === 'server' ? 'servers' : 'tools'} — the grid is legible as drawn. At two hundred
          people it would not be: the row axis would become TEAMS with the people behind a
          drill-down, because two hundred 8px rows is a texture, not a chart.
        </>
      }
    >
      {grid.rows.length === 0 ? (
        <p className="hint">NO TOOL CALLS RECORDED IN THIS WINDOW.</p>
      ) : asTable ? (
        <div className="tablewrap">
          <table className="chtable">
            <thead>
              <tr>
                <th>Person</th>
                {grid.cols.map((c) => (
                  <th key={c} className="num">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((r) => (
                <tr key={r}>
                  <td>
                    <b>{r}</b>
                  </td>
                  {grid.cols.map((c) => {
                    const v = grid.cells.find((x) => x.row === r && x.col === c)?.value ?? 0
                    return (
                      <td key={c} className="num">
                        {v === 0 ? '—' : fmtInt(v)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Heatmap
          rows={grid.rows}
          cols={grid.cols}
          cells={grid.cells}
          surface={theme.panel}
          hue={slotColor(theme.chartColors, 0)}
          format={fmtInt}
          unit="tool calls"
          label={`Tool calls per person by ${axis}`}
        />
      )}
      {/* THE TABLE VIEW IS NOT OPTIONAL. A continuous colour scale is the one
          encoding a reader cannot check by eye, so every value has to be
          reachable without hovering. */}
      <div className="metricpick" style={{ marginTop: 12 }}>
        <button type="button" data-on={asTable ? '0' : '1'} onClick={() => setAsTable(false)}>
          Heatmap
        </button>
        <button type="button" data-on={asTable ? '1' : '0'} onClick={() => setAsTable(true)}>
          Table view
        </button>
      </div>
    </Card>
  )
}

/* ==========================================================================
 * 3b. THE TOPIC TREND PANEL — the front page's "what is this being used for"
 * ======================================================================== */

/**
 * WHAT THIS IS INSTEAD OF, AND WHY. The ask was "some sort of word cloud or
 * trend panel". A word cloud is not a design choice here, it is a PRIVACY one:
 * the taxonomy classifier reads conversation TITLES, stores only counts, and
 * never writes a title down — a promise the method panel on the Taxonomy view
 * makes in as many words. A cloud of title fragments would put raw text from
 * private conversations onto an all-staff board, which is a different product
 * with a different approval. So this draws the counts that already exist:
 * `daily_topics`, one row per day per label, no new data path and no new
 * exposure.
 *
 * It is also the better chart. A word cloud encodes one variable (frequency)
 * in the least accurately-readable channel there is — area, of glyphs of
 * different lengths — and cannot show direction at all. This shows both, and
 * every label on it came from a fixed list of eleven rather than from
 * somebody's sentence.
 */
export function TopicTrendPanel({ model, theme }: { model: BoardModel; theme: ArcadeTheme }) {
  const colors = theme.chartColors
  const topics = model.topics
  if (topics.length === 0) {
    return (
      <Card title="What the platform is being used for" sub="From the nightly topic classifier.">
        <p className="hint">NO TOPIC DATA IN THIS WINDOW.</p>
      </Card>
    )
  }

  const total = topics.reduce((n, t) => n + t.count, 0)
  // Keyed on the DAY LIST from the topic table, which the server bounds by the
  // window's `topicDays` — so this panel is window-driven like every other one.
  const days = [...new Set(model.topicDaily.map((d) => d.day))].sort()
  const seriesFor = (topic: string): number[] => {
    const byDay = new Map(model.topicDaily.filter((d) => d.topic === topic).map((d) => [d.day, d.count]))
    return days.map((d) => byDay.get(d) ?? 0)
  }
  const head = topics.slice(0, TREND_TOPICS)

  return (
    <Card
      title="What the platform is being used for"
      sub="Conversation topics by volume, and the shape of each one over the window. Titles only — no message content reaches the classifier and no title is ever stored."
      note={
        <>
          <b>This is the trend panel, and it is deliberately not a word cloud.</b> The classifier
          reads titles and writes down counts; a cloud would put fragments of those titles on an
          all-staff board, which is an exposure decision rather than a design one. Every label here
          comes from a fixed list of eleven. The full breakdown, and what the labelling cannot tell
          you, is on the TAXONOMY view.
        </>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <TopicShareBar topics={topics} colors={colors} named={TREND_TOPICS} />
      </div>
      {days.length > 1 ? (
        <div className="smgrid">
          {head.map((t) => (
            <div className="smcard" key={t.topic}>
              <div className="smn">
                <span>{t.topic}</span>
                <b>{fmtInt(t.count)}</b>
              </div>
              {/* One hue across the grid: in a small multiple the card title
                  carries identity, so a per-card colour would encode nothing
                  and would key on rank order. */}
              <SmallMultiple values={seriesFor(t.topic)} color={slotColor(colors, 0)} label={`${t.topic} over time`} />
              <div className="sms">{total > 0 ? `${fmtPct(t.count / total)} of the platform` : ''}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="hint">
          NO DAY-BY-DAY TOPIC SHAPE IN A {model.window.toUpperCase()} WINDOW. The classifier is a
          NIGHTLY batch with no sub-day resolution, so every window shorter than a day reads the
          same single day of labels. The share bar above is that day; the shapes need WK or longer.
        </p>
      )}
    </Card>
  )
}

/** Group rows for the sub-floor charts. Every group, hatched below the floor,
 *  with its `n` printed — secondary encoding rather than suppression. */
function groupChart(players: BoardPlayer[], kind: 'office' | 'team', floor: number) {
  const model = buildRivalry(players, kind, [], floor)
  return model.sides.map((s: RivalryStanding) => ({
    label: s.name,
    value: s.score,
    note: `n=${s.active}${s.thin ? ' · thin' : ''}`,
    hatched: s.thin,
  }))
}

/* ==========================================================================
 * 4. TAXONOMY
 * ======================================================================== */

/** How many topics keep their own donut segment before the tail is pooled. */
const DONUT_NAMED = 6

/**
 * The taxonomy, with the method panel that is the point of the rework.
 *
 * The donut is legal here because the question is genuinely part-to-whole at a
 * glance and the tail is pooled so nothing is too thin to see — and it is
 * PAIRED with a ranked bar chart, because a ring cannot resolve one mid-sized
 * segment against another and a bar chart can.
 *
 * The method panel exists because this view has never explained itself, and
 * that is why people mistrust it. It states what the classifier does and, more
 * importantly, what it does not claim.
 */
export function TaxonomyView({
  model,
  theme,
  tab,
  onSelectTopic,
}: {
  model: BoardModel
  theme: ArcadeTheme
  tab: string
  onSelectTopic: (topic: string) => void
}) {
  const colors = theme.chartColors
  const topics = model.topics
  if (topics.length === 0) return <p className="hint">NO TOPIC DATA IN THIS WINDOW.</p>

  const total = topics.reduce((n, t) => n + t.count, 0)
  const head = topics.slice(0, DONUT_NAMED)
  const tail = topics.slice(DONUT_NAMED)
  const tailCount = tail.reduce((n, t) => n + t.count, 0)
  const segments = [
    ...head.map((t) => ({ label: t.topic, value: t.count })),
    ...(tailCount > 0 ? [{ label: `${tail.length} more`, value: tailCount }] : []),
  ]
  // The pooled tail takes the NEUTRAL, not the next slot. Giving it a hue put
  // two adjacent reds in the ring on the first render, which reads as two
  // categories that happen to look identical.
  const segmentColor = (i: number) => (i < head.length ? slotColor(colors, i) : NEUTRAL_SERIES)

  // Per-topic daily series for the small multiples, keyed on the day list so
  // every card shares an x range even where a topic has no row for a day.
  const days = [...new Set(model.topicDaily.map((d) => d.day))].sort()
  const seriesFor = (topic: string): number[] => {
    const byDay = new Map(model.topicDaily.filter((d) => d.topic === topic).map((d) => [d.day, d.count]))
    return days.map((d) => byDay.get(d) ?? 0)
  }
  /** Last 7 days against the 7 before. On this much history that is a SHAPE,
   *  not a trend, and the note under the grid says so. */
  const direction = (values: number[]): string => {
    if (values.length < 4) return ''
    const half = Math.min(7, Math.floor(values.length / 2))
    const recent = values.slice(-half).reduce((n, v) => n + v, 0)
    const prior = values.slice(-half * 2, -half).reduce((n, v) => n + v, 0)
    if (prior === 0 && recent === 0) return 'flat'
    if (prior === 0) return 'new'
    const change = (recent - prior) / prior
    if (Math.abs(change) < 0.1) return 'level'
    return change > 0 ? `up ${fmtPct(change)}` : `down ${fmtPct(-change)}`
  }

  if (tab === 'trend') {
    return (
      <Card
        title="Every topic, over time"
        sub="One small multiple per topic on its own scale, so the shape of a topic is readable even when its volume is small. The counts are on the EVERY TOPIC tab."
        note={
          days.length > 1 ? (
            <>
              Direction compares the recent half of the window against the half before it. On this
              much history that is a <b>shape</b>, not a trend: the platform is weeks old and
              nothing here survives being called seasonality.
            </>
          ) : null
        }
      >
        {days.length > 1 ? (
          <div className="smgrid">
            {topics.map((t) => {
              const values = seriesFor(t.topic)
              return (
                <button type="button" className="smcard" key={t.topic} onClick={() => onSelectTopic(t.topic)}>
                  <div className="smn">
                    <span>{t.topic}</span>
                    <b>{fmtInt(t.count)}</b>
                  </div>
                  {/* One hue across the grid: in a small multiple the card
                      title carries identity, so a per-card colour would encode
                      nothing and would key on rank order. */}
                  <SmallMultiple values={values} color={slotColor(colors, 0)} label={`${t.topic} over time`} />
                  <div className="sms">
                    {total > 0 ? fmtPct(t.count / total) : ''} of the platform · {direction(values)}
                  </div>
                </button>
              )
            })}
          </div>
        ) : (
          <p className="hint">
            NO DAY-BY-DAY SHAPE IN A {model.window.toUpperCase()} WINDOW. The classifier is a
            NIGHTLY batch and has no sub-day resolution, so every window shorter than a day reads
            the same single day of labels — there is one point per topic and a line through one
            point is not a shape. The SUMMARY tab&rsquo;s share is that day, and it is real. Pick WK
            or longer.
          </p>
        )}
      </Card>
    )
  }

  if (tab === 'method') {
    return <TaxonomyMethodCard />
  }

  if (tab === 'table') {
    return (
      <Card
        title="Every topic, with its counts"
        sub="Click a topic for its team, contributor and model breakdown."
        note="Titles only, no message content, at every step."
      >
        <div className="tablewrap">
          <table className="chtable">
            <thead>
              <tr>
                <th>Topic</th>
                <th className="num">Conversations</th>
                <th className="num">Share</th>
                <th>Recent shape</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {topics.map((t) => (
                <tr
                  key={t.topic}
                  role="button"
                  tabIndex={0}
                  aria-label={t.topic}
                  onClick={() => onSelectTopic(t.topic)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectTopic(t.topic)
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <b>{t.topic}</b>
                  </td>
                  <td className="num">{fmtInt(t.count)}</td>
                  <td className="num">{total > 0 ? fmtPct(t.count / total) : '—'}</td>
                  <td>{days.length > 1 ? direction(seriesFor(t.topic)) : '—'}</td>
                  <td>→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 16 }}>
          <p className="dh">The whole platform as one share bar</p>
          <TopicShareBar topics={topics} colors={colors} named={DONUT_NAMED} />
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card
        title="Share of the platform"
        sub="The ring answers 'roughly how much of the whole'. The bars beside it do the comparing, because a ring is bad at 'is this one bigger than that one'."
        note="The ring is legal here because the question is genuinely part-to-whole at a glance and the tail is pooled so no segment is too thin to see. It is never shown alone: a ring cannot resolve one mid-sized segment against another, and the ranked bars can."
      >
        <div className="sharegrid">
          <div className="donutwrap">
            <Donut
              segments={segments}
              colors={segments.map((_, i) => segmentColor(i))}
              hero={fmtInt(total)}
              heroLabel="conversations classified"
              label={`Share of conversations by topic. ${topics[0]?.topic ?? ''} is the largest.`}
            />
          </div>
          <div>
            <RankedBars
              rows={topics.map((t) => ({
                label: t.topic,
                value: t.count,
                ...(total > 0 ? { note: fmtPct(t.count / total) } : {}),
              }))}
              colors={colors}
              label="Conversations by topic, ranked"
            />
          </div>
        </div>
        <ChartLegend items={segments.map((s, i) => ({ label: s.label, color: segmentColor(i) }))} />
      </Card>

    </>
  )
}

/**
 * The classifier method, as its own card and now its own TAB.
 *
 * It is the reason people trust or mistrust every number on this view, so it
 * has to be reachable rather than scrolled past. It states what the classifier
 * does and, more importantly, what it does not claim.
 */
function TaxonomyMethodCard() {
  return (
    <Card
      title="How a conversation gets its label"
      sub="The thing this view has never explained, and the reason people mistrust it."
    >
      <div className="method">
        <div>
          <p className="dh">The method</p>
          <table>
            <tbody>
              <tr>
                <td>Input</td>
                <td>
                  The auto-generated conversation <b>title only</b>. No message body ever leaves
                  Mongo.
                </td>
              </tr>
              <tr>
                <td>Classifier</td>
                <td>Claude Haiku on Bedrock, 50 titles per call, one label from a fixed list of 11.</td>
              </tr>
              <tr>
                <td>When</td>
                <td>Nightly, about 02:00 Sydney, for the previous day.</td>
              </tr>
              <tr>
                <td>Grain</td>
                <td>One label per conversation, assigned on the day it was created.</td>
              </tr>
              <tr>
                <td>Stored</td>
                <td>
                  Counts only. Titles are never written down, so a label cannot be audited after
                  the fact.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <p className="dh">What it cannot tell you</p>
          <ul>
            <li>
              <b>A conversation is labelled once, at birth.</b> One that starts as a claims
              question and turns into a spreadsheet exercise keeps its day-one label forever.
            </li>
            <li>
              <b>&ldquo;Other&rdquo; conflates two different things.</b> Some of it is genuinely
              miscellaneous; some is a title too thin to classify. The two are indistinguishable
              in the data, so an Other count is not a category count.
            </li>
            <li>
              <b>A whole batch can fail silently.</b> On any JSON or length mismatch the
              classifier returns &ldquo;Other&rdquo; for all 50, which looks like a category
              count rather than a failure. Nothing detects it.
            </li>
            <li>
              <b>The model column is 43% unknown</b>, because agent conversations carry no model.
              Any topic-by-model chart is missing the agent half of the platform, so this view
              says so rather than drawing it.
            </li>
          </ul>
        </div>
      </div>
    </Card>
  )
}

/* ==========================================================================
 * 5. THE KNOWLEDGE BASE
 *
 * Two halves, two sources, and they degrade independently because they fail
 * independently:
 *
 *  - WHO SEARCHED comes from `daily_mcp_tool_usage`. A knowledge base search
 *    IS an MCP tool call, so per-person, per-corpus, per-day usage was
 *    already in the warehouse the moment round two landed. No new data path.
 *  - WHAT IS IN THE CORPUS comes from `manual.chunks` in the same database.
 *    Counts, titles, corpora and timestamps only: never `content`, never
 *    `context`, never an embedding.
 *
 * WHAT IS DELIBERATELY ABSENT. No query text, ever. A search string is a
 * colleague's own sentence and this board goes to every member of staff — the
 * same exposure decision the word cloud was declined for at this population
 * size, and for the same reason. The topic dimension here is the CORPUS and
 * the DOCUMENT, both of which are catalogue entries somebody wrote on
 * purpose.
 * ======================================================================== */

/** The three corpora, in the order the panel prefers to list them when it has
 *  no volume to sort by. Not a whitelist: an unrecognised corpus is drawn as
 *  itself, the same rule `arcadeGroups.ts` follows for an unknown office. */
const RAG_CORPUS_ORDER = ['underwriting', 'claims', 'engineering']

function corpusRank(name: string): number {
  const at = RAG_CORPUS_ORDER.indexOf(name)
  return at < 0 ? RAG_CORPUS_ORDER.length : at
}

export function RagView({ model, theme, tab }: { model: BoardModel; theme: ArcadeTheme; tab: string }) {
  const colors = theme.chartColors
  const rag = model.rag
  const hasDaily = supportsDailySeries(model)

  // One line per corpus over the window's own day axis. Sparse by nature: the
  // eval runs nightly and real searches do not, so most days are small
  // numbers and a couple are not.
  const dailyByCorpus = useMemo(() => {
    const names = [...new Set(rag.daily.map((d) => d.corpus))].sort(
      (a, b) => corpusRank(a) - corpusRank(b),
    )
    return names.map((corpus, i) => ({
      name: corpus,
      color: slotColor(colors, i),
      values: model.days.map(
        (day) => rag.daily.find((d) => d.day === day && d.corpus === corpus)?.searches ?? 0,
      ),
    }))
  }, [rag.daily, model.days, colors])

  if (tab === 'corpora') {
    return (
      <Card
        title="The corpora"
        sub="What is indexed, how it is cut up, and when it was last looked at. Sizes are chunks and documents, which are different units: a page splits into as many chunks as its headings demand."
        note={
          rag.corpusAvailable ? (
            <>
              <b>Chunks are not documents and the two do not convert.</b> Chunking splits on
              headings, merges anything under sixty words into the section above it and windows
              anything over nine hundred, so a long page can be one chunk and a short one can be
              several. <b>Last changed</b> is the newest source page in the corpus; <b>last
              synced</b> is the newest ingest. They answer different questions, and a gap between
              them is the interesting number.
            </>
          ) : null
        }
      >
        {!rag.corpusAvailable ? (
          <p className="hint">
            THE VECTOR STORE COULD NOT BE READ. That is almost certainly the grant rather than the
            data: the store lives in the schema <b>manual</b> in this same database, and the
            panel&rsquo;s read-only role has never been granted USAGE on it. A missing grant on
            this platform produces no error, no alarm and no log line &mdash; so this panel says
            &ldquo;could not read&rdquo; rather than drawing an empty corpus, because the two are
            different claims.
          </p>
        ) : (
          <>
            <RankedBars
              rows={[...rag.corpora]
                .sort((a, b) => b.chunks - a.chunks)
                .map((c) => ({
                  label: c.corpus,
                  value: c.chunks,
                  note: `${c.documents} ${c.documents === 1 ? 'document' : 'documents'}`,
                }))}
              colors={colors}
              format={fmtInt}
              label="Chunks per corpus"
            />
            <div className="tablewrap" style={{ marginTop: 14 }}>
              <table className="chtable">
                <thead>
                  <tr>
                    <th>Corpus</th>
                    <th className="num">Documents</th>
                    <th className="num">Chunks</th>
                    <th className="num">Chunks per document</th>
                    <th>Last changed</th>
                    <th>Last synced</th>
                    <th>Chunk context</th>
                  </tr>
                </thead>
                <tbody>
                  {[...rag.corpora]
                    .sort((a, b) => corpusRank(a.corpus) - corpusRank(b.corpus))
                    .map((c) => (
                      <tr key={c.corpus}>
                        <td>
                          <b>{c.corpus}</b>
                        </td>
                        <td className="num">{fmtInt(c.documents)}</td>
                        <td className="num">{fmtInt(c.chunks)}</td>
                        <td className="num">
                          {c.documents === 0 ? '—' : (c.chunks / c.documents).toFixed(1)}
                        </td>
                        <td>{c.lastUpdated ?? '—'}</td>
                        <td>{c.lastSynced ?? '—'}</td>
                        <td>{c.contextual ? 'model-written' : 'none'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    )
  }

  if (tab === 'searches') {
    return (
      <Card
        title="Who searches the knowledge base"
        sub="Counted from the tool calls the warehouse already records. A search that returned nothing is still a search, and is counted here."
        note={
          <>
            <b>No query text appears on this board and none ever will.</b> A search string is a
            colleague&rsquo;s own sentence, and this page is linked to every member of staff &mdash;
            the same decision the word cloud was declined on. What is shown is which CORPUS was
            reached, which is a catalogue entry rather than anybody&rsquo;s words.
          </>
        }
      >
        {!rag.available ? (
          <p className="hint">
            THE TOOL TABLES COULD NOT BE READ. Not the same claim as &ldquo;nobody searched&rdquo;.
          </p>
        ) : rag.searchers.length === 0 ? (
          <p className="hint">NO KNOWLEDGE BASE SEARCHES IN THIS WINDOW.</p>
        ) : (
          <>
            <RankedBars
              rows={rag.searchers.map((s) => ({
                label: s.name,
                value: s.searches,
                note: `${s.corpora} ${s.corpora === 1 ? 'corpus' : 'corpora'}`,
              }))}
              colors={colors}
              format={fmtInt}
              label="Knowledge base searches per person"
            />
            <div className="tablewrap" style={{ marginTop: 14 }}>
              <table className="chtable narrow">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th className="num">Searches</th>
                    <th className="num">Corpora reached</th>
                  </tr>
                </thead>
                <tbody>
                  {rag.searchers.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <b>{s.name}</b>
                      </td>
                      <td className="num">{fmtInt(s.searches)}</td>
                      <td className="num">{s.corpora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    )
  }

  if (tab === 'method') {
    return (
      <Card
        title="Where this comes from, and what is missing"
        sub="Two sources, two failure modes, and three questions this board cannot answer yet."
      >
        <p className="csub">
          <b>Usage</b> is <code>daily_mcp_tool_usage</code>, filtered to the three retrieval tools
          on the chu-data server. A knowledge base search is an MCP tool call like any other, so
          the nightly export has been recording who searched what, and how often, since round two.
          No new instrumentation was needed and none was added.
        </p>
        <p className="csub" style={{ marginTop: 10 }}>
          <b>Corpus size and freshness</b> is the vector store itself, in the schema <b>manual</b>{' '}
          of this same database. The panel selects counts, page titles, corpora and timestamps. It
          never selects the chunk text, the model-written context or an embedding, and there is no
          code path here that could.
        </p>
        <p className="csub" style={{ marginTop: 10 }}>
          <b>Three things are not here, and each for a stated reason.</b>
        </p>
        <ul className="csub" style={{ marginTop: 6, paddingLeft: 18 }}>
          <li>
            <b>Retrieval quality over time.</b> The nightly eval publishes its pass rate to
            CloudWatch and nowhere else. An all-staff page must not start a metric query on load,
            so this needs the nightly export to copy those figures into a table first &mdash; the
            same pattern the tool tables already use. Until it does, the number lives in the alarm,
            not here.
          </li>
          <li>
            <b>Dead documentation &mdash; pages no search has ever returned.</b> Nothing anywhere
            records which chunks a retrieval returned: the search logs a result COUNT, and the tool
            emitter deliberately carries no arguments and no output. Answering it needs one line
            emitted per search naming the chunks it returned, and a nightly extract of those lines.
            That is a small change in one function, and it is not in this round.
          </li>
          <li>
            <b>How often the relevance floor rejects everything.</b> It is logged &mdash; the search
            writes a line when the best candidate falls under the floor &mdash; but only as prose in
            a log group with thirty days of retention, so it is queryable and not countable. The
            same one-line record above would carry it for free.
          </li>
        </ul>
      </Card>
    )
  }

  const evalNote = (
    <p className="csub" style={{ marginTop: 12 }}>
      Search volume is worth reading against the eval&rsquo;s own: the nightly retrieval eval
      issues around forty embeddings every night whether or not anybody asks it anything. If the
      staff figure above is smaller than that, the knowledge base is being exercised more by its
      own test than by the people it was built for, and that is an adoption finding rather than a
      retrieval one.
    </p>
  )

  return (
    <>
      <div className="kpis">
        <Kpi
          label="Searches this window"
          value={rag.available ? fmtInt(rag.totalSearches) : '—'}
          detail={rag.available ? `${rag.totalPeople} people searched` : 'tool tables could not be read'}
        />
        <Kpi
          label="Corpora indexed"
          value={rag.corpusAvailable ? fmtInt(rag.corpora.length) : '—'}
          detail={
            rag.corpusAvailable
              ? rag.corpora.map((c) => c.corpus).join(', ')
              : 'vector store could not be read'
          }
        />
        <Kpi
          label="Chunks indexed"
          value={rag.corpusAvailable ? fmtInt(rag.corpora.reduce((n, c) => n + c.chunks, 0)) : '—'}
          detail={
            rag.corpusAvailable
              ? `across ${fmtInt(rag.corpora.reduce((n, c) => n + c.documents, 0))} documents`
              : 'needs a grant on the manual schema'
          }
        />
        <Kpi
          label="Searches per searcher"
          value={
            rag.available && rag.totalPeople > 0
              ? (rag.totalSearches / rag.totalPeople).toFixed(1)
              : '—'
          }
          detail="in this window"
        />
      </div>

      <Card
        title="Searches by corpus"
        sub="Which body of knowledge people actually reach for. Counted by tool call, so a question answered from two corpora is two searches."
        note={
          rag.available && rag.byCorpus.length === 0 ? (
            <>
              No searches in this window. The corpora are still indexed and still reachable &mdash;
              this is a reading about use, not about the knowledge base.
            </>
          ) : null
        }
      >
        {!rag.available ? (
          <p className="hint">THE TOOL TABLES COULD NOT BE READ.</p>
        ) : (
          <RankedBars
            rows={[...rag.byCorpus]
              .sort((a, b) => b.searches - a.searches)
              .map((c) => ({
                label: c.corpus,
                value: c.searches,
                note: `${c.people} ${c.people === 1 ? 'person' : 'people'}`,
              }))}
            colors={colors}
            format={fmtInt}
            label="Searches per corpus"
          />
        )}
        {evalNote}
      </Card>

      <Card
        title="Searches per day"
        sub="One line per corpus, one axis, over the window on the pills above."
      >
        {!hasDaily || dailyByCorpus.length === 0 ? (
          <WindowTooShort form="SEARCH SERIES" model={model} />
        ) : (
          <>
            <LineChart
              series={dailyByCorpus}
              xLabels={model.days.map((d) => d.slice(5))}
              format={fmtInt}
              label="Knowledge base searches per day by corpus"
            />
            <ChartLegend
              shape="line"
              items={dailyByCorpus.map((s) => ({ label: s.name, color: s.color }))}
            />
          </>
        )}
      </Card>
    </>
  )
}
